import { authenticatedFetch } from "./apiClient";
import {
  ApiRequestError,
  parseJsonOrThrow,
  throwFromErrorBody,
} from "../utils/apiRequestError";
import { parseApiErrorBody } from "../utils/parseApiError";
import { pollOhlcStripUntilDone } from "../utils/executionCandlesLoad";
import type {
  StrategyRdAppliedLevels,
  StrategyRdApplyHypothesisRequest,
  StrategyRdApplyOverview,
  StrategyRdApplyResponse,
  StrategyRdApplyResult,
  StrategyRdCatalog,
  StrategyRdChartParams,
  StrategyRdClosedFill,
  StrategyRdComposerConfluenceSlot,
  StrategyRdConcept,
  StrategyRdConceptSetting,
  StrategyRdConfirmRequest,
  StrategyRdConfirmResponse,
  StrategyRdCreateHypothesisRequest,
  StrategyRdEntryMode,
  StrategyRdEntryRuleSpec,
  StrategyRdEquityCurvePoint,
  StrategyRdExcursion,
  StrategyRdExcursionUnit,
  StrategyRdExitLeg,
  StrategyRdStopStep,
  StrategyRdManageRule,
  StrategyRdRuleCompileResult,
  StrategyRdRuleExpr,
  StrategyRdRuleRole,
  StrategyRdRuleVocabulary,
  StrategyRdRuleVocabularySlot,
  StrategyRdFill,
  StrategyRdForwardOutcome,
  StrategyRdHypothesis,
  StrategyRdInstrument,
  StrategyRdInstrumentsResponse,
  StrategyRdIntrabarMode,
  StrategyRdBacktestWarning,
  StrategyRdCurrencyInfo,
  StrategyRdMarginCall,
  StrategyRdMarginSummary,
  StrategyRdParity,
  StrategyRdPercentiles,
  StrategyRdPineScript,
  StrategyRdRobustness,
  StrategyRdLastApply,
  StrategyRdMatch,
  StrategyRdOffsetKind,
  StrategyRdOhlcBar,
  StrategyRdOhlcStats,
  StrategyRdOutcomeSummary,
  StrategyRdPack,
  StrategyRdPeriodPnlBucket,
  StrategyRdPreset,
  StrategyRdPreviewEmptyState,
  StrategyRdPreviewEmptyStateAction,
  StrategyRdPreviewConceptsRequest,
  StrategyRdPreviewConceptsResponse,
  StrategyRdResearchSimPayload,
  StrategyRdResearchSimResponse,
  StrategyRdResolveRequest,
  StrategyRdResolveResponse,
  StrategyRdRuleTemplate,
  StrategyRdScanContext,
  StrategyRdScanRequest,
  StrategyRdScanResponse,
  StrategyRdScanStatus,
  StrategyRdSessionOption,
  StrategyRdSessionsPayload,
  StrategyRdSizing,
  StrategyRdStopAnchor,
  StrategyRdStopDistanceBound,
  StrategyRdTargetMode,
  StrategyRdHeadlineStats,
  StrategyRdHoldoutSplit,
  StrategyRdStability,
  StrategyRdHalves,
  StrategyRdConcentration,
  StrategyRdMonthBucket,
  StrategyRdCostStress,
  StrategyRdCostStressVariant,
  StrategyRdSweepTable,
  StrategyRdHypothesisVersion,
  StrategyRdRuleBlock,
  StrategyRdRuleBlockUse,
  StrategyRdPreviewPathRequest,
  StrategyRdPreviewPathResponse,
  StrategyRdMatchPath,
} from "../types/strategyRd";
import { STRATEGY_RD_API_BASE, STRATEGY_RD_DISABLED_SLUG } from "../types/strategyRd";
import { parseStrategyRdReport } from "../utils/strategyRdReport";
import type { StrategyRdCoachResultCard } from "../types/strategyRdCoach";
import { normalizeStrategyRdForwardResult } from "../utils/strategyRd";
import {
  compactNestedComposerFields,
  parseStrategyRdLogicPath,
  strategyRdConfluenceIds,
  strategyRdConfluenceSlotsForRequest,
  strategyRdMaxWaitForWire,
} from "../utils/strategyRdComposer";
import { parseStrategyRdEntryGuidance, parseStrategyRdMatchPath } from "../utils/strategyRdPath";
import {
  isStrategyRdJobTerminalStatus,
  strategyRdApiPathFromPollUrl,
  strategyRdJobPollIntervalMs,
  strategyRdJobProgressMessage,
  strategyRdJobStatusLabel,
  type StrategyRdJobKind,
  type StrategyRdJobLifecycleStatus,
  type StrategyRdJobProgress,
} from "../utils/strategyRdJobs";
import {
  normalizeStrategyRdSrScopes,
  strategyRdSrScopesForRequest,
} from "../utils/strategyRdConcepts";
import {
  parseStrategyRdMatchMarkers,
  resolveStrategyRdMatchMarkers,
} from "../utils/strategyRdDetect";
import { parseStrategyRdSrRails } from "../utils/strategyRdConceptSr";
import { parseStrategyRdPineExportInfo } from "../utils/strategyRdPineExport";
import {
  parseStrategyRdConditionChoices,
  type StrategyRdConditionChoices,
} from "../utils/strategyRdConditionSetup";

export type { StrategyRdJobKind, StrategyRdJobLifecycleStatus, StrategyRdJobProgress };

export class StrategyRdDisabledError extends Error {
  slug = STRATEGY_RD_DISABLED_SLUG;
  status = 404;

  constructor(message = "Strategy Lab research is not available.") {
    super(message);
    this.name = "StrategyRdDisabledError";
  }
}

export function isStrategyRdDisabledError(err: unknown): err is StrategyRdDisabledError {
  return err instanceof StrategyRdDisabledError;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function asOptionalString(value: unknown): string | null | undefined {
  if (value == null) return value as null | undefined;
  const s = asString(value).trim();
  return s || null;
}

function asNumber(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function parseChartParams(raw: unknown): StrategyRdChartParams {
  const row = asRecord(raw) ?? {};
  return {
    symbol: asString(row.symbol).trim(),
    broker_account_code: asString(row.broker_account_code).trim(),
    candle_type: asString(row.candle_type).trim(),
    start_date: asString(row.start_date).trim(),
    end_date: asString(row.end_date).trim(),
    include_trades: row.include_trades === true,
    fill_missing: row.fill_missing !== false,
    asset_class: asOptionalString(row.asset_class) ?? undefined,
    market_data_broker: asOptionalString(row.market_data_broker),
    md_source: asOptionalString(row.md_source),
    md_listing_key: asOptionalString(row.md_listing_key),
  };
}

function parseMatch(raw: unknown): StrategyRdMatch | null {
  const row = asRecord(raw);
  if (!row) return null;
  const barTime = asString(row.bar_time).trim();
  const patternId =
    asString(row.pattern_id).trim() || asString(row.setup_id).trim();
  if (!barTime || !patternId) return null;
  const directionRaw = asString(row.direction).trim().toLowerCase();
  const direction =
    directionRaw === "short" ? "short" : directionRaw === "neutral" ? "neutral" : "long";
  const meta = asRecord(row.meta);
  const path = parseStrategyRdMatchPath(row.path);
  return {
    pattern_id: patternId,
    setup_id: asOptionalString(row.setup_id) ?? patternId,
    symbol: asString(row.symbol).trim(),
    candle_type: asString(row.candle_type).trim(),
    bar_time: barTime,
    direction,
    meta,
    path,
    md_source: asOptionalString(row.md_source),
    md_listing_key: asOptionalString(row.md_listing_key),
    market_data_broker: asOptionalString(row.market_data_broker),
    chart: row.chart ? parseChartParams(row.chart) : undefined,
  };
}

function parseOhlcStats(raw: unknown): StrategyRdOhlcStats {
  const row = asRecord(raw) ?? {};
  const statusRec = asRecord(row.volume_status);
  const volumeAvailableRaw = row.volume_available;
  const volumeAvailable =
    typeof volumeAvailableRaw === "boolean"
      ? volumeAvailableRaw
      : typeof statusRec?.available === "boolean"
        ? statusRec.available
        : null;
  const volumeStatus =
    statusRec != null
      ? {
          available:
            typeof statusRec.available === "boolean" ? statusRec.available : volumeAvailable,
          reason: asOptionalString(statusRec.reason) ?? null,
        }
      : volumeAvailable != null
        ? {
            available: volumeAvailable,
            reason: volumeAvailable ? null : "volume_unavailable",
          }
        : null;
  return {
    ...row,
    ...(volumeAvailable != null ? { volume_available: volumeAvailable } : {}),
    ...(volumeStatus ? { volume_status: volumeStatus } : {}),
  };
}

function parseOhlcBars(raw: unknown): StrategyRdOhlcBar[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdOhlcBar[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) continue;
    const timestamp = asString(row.timestamp || row.time || row.bar_time).trim();
    const open = asNumber(row.open, Number.NaN);
    const high = asNumber(row.high, Number.NaN);
    const low = asNumber(row.low, Number.NaN);
    const close = asNumber(row.close, Number.NaN);
    if (!timestamp || ![open, high, low, close].every(Number.isFinite)) continue;
    const volumeRaw = row.volume;
    const volume =
      volumeRaw == null || volumeRaw === ""
        ? null
        : (() => {
            const n = asNumber(volumeRaw, Number.NaN);
            return Number.isFinite(n) ? n : null;
          })();
    out.push({
      timestamp,
      open,
      high,
      low,
      close,
      ...(volume != null ? { volume } : {}),
    });
  }
  return out;
}

export function parseStrategyRdScanResponse(raw: unknown, fallbackStatus: StrategyRdScanStatus): StrategyRdScanResponse {
  const row = asRecord(raw) ?? {};
  const statusRaw = asString(row.status).trim().toLowerCase();
  const status: StrategyRdScanStatus =
    statusRaw === "backfilling" || fallbackStatus === "backfilling" ? "backfilling" : "ready";
  const matchesRaw = Array.isArray(row.matches) ? row.matches : [];
  const matches = matchesRaw
    .map(parseMatch)
    .filter((m): m is StrategyRdMatch => m != null);
  const sessionIds = Array.isArray(row.session_ids)
    ? row.session_ids.map((s) => asString(s).trim().toLowerCase()).filter(Boolean)
    : undefined;
  const confluences = Array.isArray(row.confluences)
    ? row.confluences.map((c) => asString(c).trim()).filter(Boolean)
    : undefined;
  const windowChart = row.chart
    ? parseChartParams(row.chart)
    : row.chart_hint
      ? parseChartParams(row.chart_hint)
      : undefined;
  return {
    status,
    pattern_id: asString(row.pattern_id).trim() || asString(row.setup_id).trim(),
    setup_id: asOptionalString(row.setup_id),
    symbol: asString(row.symbol).trim(),
    candle_type: asString(row.candle_type).trim(),
    lookback_days: asNumber(row.lookback_days, 30),
    matches,
    match_markers: resolveStrategyRdMatchMarkers(
      parseStrategyRdMatchMarkers(row.match_markers),
      matches
    ),
    ohlc_stats: parseOhlcStats(row.ohlc_stats),
    resolved_symbol: asOptionalString(row.resolved_symbol),
    md_source: asOptionalString(row.md_source),
    md_listing_key: asOptionalString(row.md_listing_key),
    market_data_broker: asOptionalString(row.market_data_broker),
    strip_id: asOptionalString(row.strip_id) ?? undefined,
    poll_url: asOptionalString(row.poll_url) ?? undefined,
    job_poll_url: asOptionalString(row.job_poll_url) ?? undefined,
    user_message: asOptionalString(row.user_message) ?? undefined,
    candles: parseOhlcBars(row.candles),
    scan_job_id: asOptionalString(row.scan_job_id),
    candles_total: typeof row.candles_total === "number" ? row.candles_total : null,
    candles_trimmed: row.candles_trimmed === true,
    window_first_bar: asOptionalString(row.window_first_bar),
    chart: windowChart,
    chart_hint: row.chart_hint ? parseChartParams(row.chart_hint) : windowChart,
    session_ids: sessionIds && sessionIds.length > 0 ? sessionIds : undefined,
    confluences: confluences && confluences.length > 0 ? confluences : undefined,
    path_preview: row.path_preview === true,
    sr_scopes: Array.isArray(row.sr_scopes)
      ? normalizeStrategyRdSrScopes(row.sr_scopes)
      : row.sr_scopes === null
        ? null
        : undefined,
    sr_rails: parseStrategyRdSrRails(row.sr_rails),
    composer: asRecord(row.composer),
    logic_path: parseStrategyRdLogicPath(row.logic_path),
    entry_guidance: parseStrategyRdEntryGuidance(row.entry_guidance),
    multi_tf: row.multi_tf === true,
    pine_multi_tf_supported: row.pine_multi_tf_supported === true,
    chart_by_tf:
      row.chart_by_tf && typeof row.chart_by_tf === "object" && !Array.isArray(row.chart_by_tf)
        ? (row.chart_by_tf as Record<string, StrategyRdChartParams | Record<string, unknown>>)
        : null,
    empty_state: parsePreviewEmptyState(row.empty_state),
  };
}

function parsePreviewEmptyState(raw: unknown): StrategyRdPreviewEmptyState | null {
  const r = asRecord(raw);
  if (!r) return null;
  const code = asOptionalString(r.code);
  const title = asOptionalString(r.title);
  const body = asOptionalString(r.body_markdown);
  if (!code || !title || !body) return null;
  const actionsRaw = Array.isArray(r.suggested_actions) ? r.suggested_actions : [];
  const suggested_actions: StrategyRdPreviewEmptyStateAction[] = [];
  for (const item of actionsRaw) {
    const a = asRecord(item);
    if (!a) continue;
    const id = asOptionalString(a.id);
    const label = asOptionalString(a.label);
    if (!id || !label) continue;
    suggested_actions.push({
      id,
      label,
      goal: asOptionalString(a.goal),
      faq_id: asOptionalString(a.faq_id),
    });
  }
  return {
    code,
    title,
    body_markdown: body,
    hints: Array.isArray(r.hints)
      ? r.hints.map((h) => asString(h).trim()).filter(Boolean)
      : null,
    suggested_actions: suggested_actions.length ? suggested_actions : null,
  };
}

function throwIfStrategyRdDisabled(response: Response, body: unknown): void {
  if (response.status !== 404) return;
  const parsed = parseApiErrorBody(
    body && typeof body === "object" ? (body as Record<string, unknown>) : {},
    "Strategy Lab research is not available."
  );
  if (parsed.slug === STRATEGY_RD_DISABLED_SLUG) {
    throw new StrategyRdDisabledError(parsed.message);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => resolve(), ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function waitWhileDocumentHidden(signal?: AbortSignal): Promise<void> {
  if (typeof document === "undefined") return;
  while (document.visibilityState === "hidden") {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await new Promise<void>((resolve, reject) => {
      const onVis = () => {
        cleanup();
        resolve();
      };
      const onAbort = () => {
        cleanup();
        reject(new DOMException("Aborted", "AbortError"));
      };
      const cleanup = () => {
        document.removeEventListener("visibilitychange", onVis);
        signal?.removeEventListener("abort", onAbort);
      };
      document.addEventListener("visibilitychange", onVis);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
  }
}

/** Map failed job `{ error: { code, message, ... } }` to the same UX as HTTP 4xx bodies. */
export function throwStrategyRdJobFailed(payload: unknown, fallbackMessage: string): never {
  const row = asRecord(payload) ?? {};
  const err = asRecord(row.error) ?? {};
  const code = asString(err.code || err.error).trim() || "strategy_rd.job_failed";
  const message =
    asString(err.message).trim() ||
    asString(row.message).trim() ||
    fallbackMessage;
  const httpStatus = asNumber(err.http_status, 400);
  const details = asRecord(err.details);
  const body: Record<string, unknown> = {
    error: code,
    message,
  };
  if (details) {
    body.details = details;
  } else {
    // Preserve quote-variant candidates if BE nested them on the error object.
    const candidates = err.quote_variant_candidates ?? row.quote_variant_candidates;
    if (candidates != null) {
      body.details = {
        quote_variant_candidates: candidates,
        requires_quote_variant_choice: true,
      };
    }
  }
  throwFromErrorBody(httpStatus, body, fallbackMessage);
}

export type EnqueueAndPollOptions = {
  signal?: AbortSignal;
  maxWaitMs?: number;
  onProgress?: (progress: StrategyRdJobProgress) => void;
  /** Statuses that end the poll loop. Apply/Confirm omit `backfilling`. */
  stopOn?: ReadonlyArray<StrategyRdJobLifecycleStatus>;
  fallbackErrorMessage?: string;
  /** Override poll backoff (tests). */
  pollIntervalMs?: (elapsedMs: number) => number;
};

/**
 * POST async Strategy Lab work → 202 → poll GET /jobs/{id} until terminal.
 * Does not assume the 202 body contains matches / apply results / strategy ids.
 */
export async function enqueueAndPollStrategyRdJob(
  path: string,
  body: unknown,
  options: EnqueueAndPollOptions = {}
): Promise<Record<string, unknown>> {
  const { signal, fallbackErrorMessage = "Strategy Lab job failed." } = options;

  const response = await authenticatedFetch(path, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
    signal,
  });

  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, fallbackErrorMessage);
  }

  if (response.status !== 202) {
    const errBody = await response.json().catch(() => ({}));
    throwFromErrorBody(
      response.status,
      errBody,
      fallbackErrorMessage
    );
  }

  const queued = asRecord(await response.json().catch(() => ({}))) ?? {};
  const jobId = asString(queued.job_id).trim();
  const pollUrlRaw =
    asOptionalString(queued.job_poll_url) ||
    asOptionalString(queued.poll_url) ||
    (jobId ? `${STRATEGY_RD_API_BASE}/jobs/${jobId}` : "");
  const pollPath = strategyRdApiPathFromPollUrl(pollUrlRaw || "");
  if (!pollPath) {
    throwFromErrorBody(500, queued, "Strategy Lab job did not return a poll URL.");
  }

  const kind = (asString(queued.kind).trim() || undefined) as StrategyRdJobKind | undefined;
  return pollStrategyRdJob(pollPath, kind, options);
}

/** Poll an already-queued job (`GET /jobs/{id}`) until terminal — see `enqueueAndPollStrategyRdJob`. */
export async function pollStrategyRdJob(
  pollUrl: string,
  kind: StrategyRdJobKind | undefined,
  options: EnqueueAndPollOptions = {}
): Promise<Record<string, unknown>> {
  const {
    signal,
    maxWaitMs = 300_000,
    onProgress,
    stopOn = ["ready", "failed"],
    fallbackErrorMessage = "Strategy Lab job failed.",
    pollIntervalMs = strategyRdJobPollIntervalMs,
  } = options;
  let pollPath = strategyRdApiPathFromPollUrl(pollUrl);
  if (!pollPath) {
    throwFromErrorBody(500, {}, "Strategy Lab job did not return a poll URL.");
  }

  onProgress?.({
    status: "queued",
    kind,
    message: strategyRdJobStatusLabel("queued", kind),
  });

  const startedAt = Date.now();

  while (Date.now() - startedAt < maxWaitMs) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await waitWhileDocumentHidden(signal);

    const pollResponse = await authenticatedFetch(pollPath, { signal });
    if (pollResponse.status === 404) {
      const errBody = await pollResponse.json().catch(() => ({}));
      throwIfStrategyRdDisabled(pollResponse, errBody);
      throwFromErrorBody(pollResponse.status, errBody, "Strategy Lab job not found.");
    }
    if (!pollResponse.ok) {
      const errBody = await pollResponse.json().catch(() => ({}));
      throwFromErrorBody(
        pollResponse.status,
        errBody,
        "Could not poll Strategy Lab job."
      );
    }

    const payload = asRecord(await pollResponse.json().catch(() => ({}))) ?? {};
    const status = asString(payload.status).trim().toLowerCase() || "queued";
    const jobKind =
      ((asString(payload.kind).trim() || kind) as StrategyRdJobKind | undefined) || undefined;
    const resultRow = asRecord(payload.result) ?? payload;
    const userMessage =
      asOptionalString(resultRow.user_message) ?? asOptionalString(payload.user_message);

    // Canonical job URL when BE echoes job_poll_url during backfill (strip poll_url is optional progress only).
    const nextJobPoll =
      asOptionalString(resultRow.job_poll_url) ?? asOptionalString(payload.job_poll_url);
    if (nextJobPoll) {
      const nextPath = strategyRdApiPathFromPollUrl(nextJobPoll);
      if (nextPath) pollPath = nextPath;
    }

    const stageRow = asRecord(payload.progress);
    const stage = stageRow
      ? {
          stage: asString(stageRow.stage).trim(),
          done: parseOptionalFinite(stageRow.done),
          total: parseOptionalFinite(stageRow.total),
        }
      : null;
    onProgress?.({
      status,
      kind: jobKind,
      message: strategyRdJobProgressMessage(status, {
        kind: jobKind,
        userMessage,
        stage,
      }),
      stage,
    });

    if (isStrategyRdJobTerminalStatus(status, stopOn)) {
      if (status === "failed") {
        throwStrategyRdJobFailed(payload, fallbackErrorMessage);
      }
      return payload;
    }

    const elapsed = Date.now() - startedAt;
    await sleep(pollIntervalMs(elapsed), signal);
  }

  throwFromErrorBody(
    408,
    {
      error: "strategy_rd.job_timeout",
      message:
        "Market data is still loading. Keep this preview open and try again in a moment.",
    },
    "Strategy Lab job timed out."
  );
}

function parsePresetRow(raw: unknown): StrategyRdPreset | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const id = asString(rec.id).trim();
  if (!id) return null;
  return {
    id,
    name: asString(rec.name).trim() || id,
    description: asString(rec.description).trim(),
  };
}

function parseStringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = raw.map((item) => asString(item).trim()).filter(Boolean);
  return out.length > 0 ? out : undefined;
}

/** Legacy ids: string[] or direction map `{ long, short }` (e.g. stage). */
function parseLegacyIds(
  raw: unknown
): string[] | Record<string, string> | null | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return parseStringList(raw) ?? null;
  const rec = asRecord(raw);
  if (!rec) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(rec)) {
    const k = String(key || "").trim();
    const v = asString(value).trim();
    if (!k || !v) continue;
    out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function parseBranchHint(raw: unknown): StrategyRdConcept["branch_hint"] {
  const rec = asRecord(raw);
  if (!rec) return null;
  const long = asOptionalString(rec.long);
  const short = asOptionalString(rec.short);
  const label = asOptionalString(rec.label);
  if (!long && !short && !label) return null;
  return { long, short, label };
}

function parseConceptSettingRow(raw: unknown): StrategyRdConceptSetting | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const key = asString(rec.key).trim();
  if (!key) return null;
  const enumRaw = Array.isArray(rec.enum_values) ? rec.enum_values : [];
  const enum_values = enumRaw
    .map((row) => {
      const er = asRecord(row);
      if (!er) return null;
      const value = asString(er.value).trim();
      if (!value) return null;
      return { value, label: asString(er.label).trim() || value };
    })
    .filter((row): row is { value: string; label: string } => row != null);
  const visibleRec = asRecord(rec.visible_when);
  let visible_when: Record<string, string | string[]> | null = null;
  if (visibleRec) {
    visible_when = {};
    for (const [k, v] of Object.entries(visibleRec)) {
      if (Array.isArray(v)) {
        visible_when[k] = v.map((x) => asString(x).trim()).filter(Boolean);
      } else if (v != null && v !== "") {
        visible_when[k] = asString(v).trim();
      }
    }
    if (Object.keys(visible_when).length === 0) visible_when = null;
  }
  const defaultRaw = rec.default;
  const defaultValue =
    defaultRaw == null || defaultRaw === ""
      ? null
      : typeof defaultRaw === "number" || typeof defaultRaw === "boolean"
        ? defaultRaw
        : asString(defaultRaw);
  return {
    key,
    label: asOptionalString(rec.label),
    value_kind: asString(rec.value_kind).trim() || "string",
    required: rec.required === true,
    default: defaultValue,
    param: rec.param === true,
    enum_values: enum_values.length > 0 ? enum_values : null,
    visible_when,
  };
}

function parseConceptSettings(raw: unknown): StrategyRdConceptSetting[] | null {
  if (!Array.isArray(raw)) return null;
  const rows = raw
    .map(parseConceptSettingRow)
    .filter((row): row is StrategyRdConceptSetting => row != null);
  return rows.length > 0 ? rows : null;
}

function parseConceptRow(raw: unknown): StrategyRdConcept | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const id = asString(rec.id).trim();
  if (!id) return null;
  const settingsArray = parseConceptSettings(rec.settings);
  // Legacy dict settings (branch_hint only) — not the catalog schema array.
  const settingsRecord = Array.isArray(rec.settings) ? null : asRecord(rec.settings);
  return {
    id,
    family: asString(rec.family).trim() || "other",
    role: asString(rec.role).trim() || "setup",
    roles: parseStringList(rec.roles),
    wave: asString(rec.wave).trim() || "A",
    name: asString(rec.name).trim() || id,
    description: asOptionalString(rec.description),
    direction_mode: asOptionalString(rec.direction_mode) ?? undefined,
    pattern_class: asOptionalString(rec.pattern_class),
    bar_span: asOptionalString(rec.bar_span),
    agnostic: rec.agnostic === true,
    legacy_ids: parseLegacyIds(rec.legacy_ids),
    family_kind: asOptionalString(rec.family_kind),
    picker_hidden: rec.picker_hidden === true ? true : undefined,
    superseded_by: asOptionalString(rec.superseded_by),
    pair_group: asOptionalString(rec.pair_group),
    branch_hint:
      parseBranchHint(rec.branch_hint) ||
      parseBranchHint(settingsRecord?.branch_hint) ||
      null,
    volume_requires: rec.volume_requires === true,
    requires_session: rec.requires_session === true ? true : undefined,
    level_extremes: rec.level_extremes === true ? true : undefined,
    location_interaction: rec.location_interaction === true ? true : undefined,
    params: asRecord(rec.params),
    settings: settingsArray,
  };
}

function parsePackRow(raw: unknown): StrategyRdPack | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const id = asString(rec.id).trim();
  const setupId = asString(rec.setup_id).trim();
  if (!id || !setupId) return null;
  const confluences = Array.isArray(rec.confluences)
    ? rec.confluences.map((c) => asString(c).trim()).filter(Boolean)
    : [];
  const sessionIds = Array.isArray(rec.session_ids)
    ? rec.session_ids.map((c) => asString(c).trim().toLowerCase()).filter(Boolean)
    : null;
  return {
    id,
    wave: asString(rec.wave).trim() || "A",
    name: asString(rec.name).trim() || id,
    setup_id: setupId,
    confluences,
    entry_mode: asString(rec.entry_mode).trim() || "signal_close",
    stop_offset_kind: asOptionalString(rec.stop_offset_kind),
    description: asOptionalString(rec.description),
    session_ids: sessionIds && sessionIds.length > 0 ? sessionIds : null,
  };
}

function parseSessionOptionRow(raw: unknown): StrategyRdSessionOption | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const id = asString(rec.id).trim().toLowerCase();
  if (!id) return null;
  const parts = Array.isArray(rec.parts)
    ? rec.parts.map((p) => asString(p).trim().toLowerCase()).filter(Boolean)
    : undefined;
  const startHour = asNumber(rec.start_hour, Number.NaN);
  const endHour = asNumber(rec.end_hour, Number.NaN);
  const sort = asNumber(rec.ui_sort_order, Number.NaN);
  const kindRaw = asString(rec.kind).trim().toLowerCase();
  const kind =
    kindRaw ||
    (parts && parts.length > 1 ? "overlap" : "") ||
    "singleton";
  return {
    id,
    name: asString(rec.name).trim() || id,
    kind,
    start_hour: Number.isFinite(startHour) ? startHour : null,
    end_hour: Number.isFinite(endHour) ? endHour : null,
    parts,
    ui_sort_order: Number.isFinite(sort) ? sort : null,
  };
}

export function parseStrategyRdSessionsPayload(raw: unknown): StrategyRdSessionsPayload | null {
  if (Array.isArray(raw)) {
    const sessions = raw
      .map(parseSessionOptionRow)
      .filter((s): s is StrategyRdSessionOption => s != null);
    if (sessions.length === 0) return null;
    return {
      sessions,
      singleton_ids: sessions.filter((s) => s.kind === "singleton").map((s) => s.id),
      all_sentinel: "all",
    };
  }
  const rec = asRecord(raw);
  if (!rec) return null;
  const sessionsRaw = Array.isArray(rec.sessions)
    ? rec.sessions
    : Array.isArray(rec.options)
      ? rec.options
      : Array.isArray(rec.items)
        ? rec.items
        : [];
  const overlapsRaw = Array.isArray(rec.overlaps) ? rec.overlaps : [];
  const seen = new Set<string>();
  const sessions: StrategyRdSessionOption[] = [];
  for (const row of [...sessionsRaw, ...overlapsRaw]) {
    const parsed = parseSessionOptionRow(row);
    if (!parsed || seen.has(parsed.id)) continue;
    seen.add(parsed.id);
    sessions.push(parsed);
  }
  if (sessions.length === 0 && !asOptionalString(rec.all_sentinel)) return null;
  const singletonIds = Array.isArray(rec.singleton_ids)
    ? rec.singleton_ids.map((s) => asString(s).trim().toLowerCase()).filter(Boolean)
    : sessions.filter((s) => s.kind !== "overlap").map((s) => s.id);
  return {
    version: asOptionalString(rec.version),
    timezone: asOptionalString(rec.timezone),
    sessions,
    singleton_ids: singletonIds,
    all_sentinel: asOptionalString(rec.all_sentinel) || "all",
    semantics: asRecord(rec.semantics),
  };
}

/** Concepts catalog (+ legacy presets array fallback). */
export function parseStrategyRdCatalogResponse(data: unknown): StrategyRdCatalog {
  if (Array.isArray(data)) {
    const presets = data
      .map(parsePresetRow)
      .filter((p): p is StrategyRdPreset => p != null);
    return {
      presets,
      concepts: presets.map((p) => ({
        id: p.id,
        family: "candle",
        role: "setup",
        wave: "A",
        name: p.name,
      })),
      packs: [],
      sessions: null,
      doc: null,
    };
  }

  const root = asRecord(data) ?? {};
  const presetsRaw = Array.isArray(root.presets) ? root.presets : [];
  const conceptsRaw = Array.isArray(root.concepts) ? root.concepts : [];
  const packsRaw = Array.isArray(root.packs) ? root.packs : [];
  const presets = presetsRaw
    .map(parsePresetRow)
    .filter((p): p is StrategyRdPreset => p != null);
  const concepts = conceptsRaw
    .map(parseConceptRow)
    .filter((c): c is StrategyRdConcept => c != null);
  const packs = packsRaw.map(parsePackRow).filter((p): p is StrategyRdPack => p != null);
  const sessions =
    parseStrategyRdSessionsPayload(root.sessions) ||
    parseStrategyRdSessionsPayload(root.session_catalog) ||
    parseStrategyRdSessionsPayload(root.session_options);

  // Legacy BE that only returns nested presets without concepts.
  const effectiveConcepts =
    concepts.length > 0
      ? concepts
      : presets.map((p) => ({
          id: p.id,
          family: "candle",
          role: "setup" as const,
          wave: "A",
          name: p.name,
        }));

  return {
    presets,
    concepts: effectiveConcepts,
    packs,
    sessions,
    doc: asOptionalString(root.doc),
  };
}

export type ListStrategyRdCatalogOptions = {
  /** Optional role filter — default list is already picker-ready. */
  role?: "setup" | "confluence" | string;
  /** Debug only — includes hidden concrete ids (bull_engulf, above_sma50, …). */
  includeHidden?: boolean;
  signal?: AbortSignal;
};

function strategyRdConceptsQuery(options?: ListStrategyRdCatalogOptions): string {
  const qs = new URLSearchParams();
  const role = (options?.role || "").trim();
  if (role) qs.set("role", role);
  if (options?.includeHidden) qs.set("include_hidden", "1");
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export async function listStrategyRdCatalog(
  signalOrOptions?: AbortSignal | ListStrategyRdCatalogOptions
): Promise<StrategyRdCatalog> {
  const options: ListStrategyRdCatalogOptions =
    signalOrOptions instanceof AbortSignal
      ? { signal: signalOrOptions }
      : signalOrOptions || {};
  const query = strategyRdConceptsQuery(options);
  // Prefer /concepts; fall back to /presets for older BE.
  // Do not invent FE hide rules — trust the default picker-ready list.
  let response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/concepts${query}`, {
    signal: options.signal,
  });
  if (response.status === 404) {
    response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/presets`, {
      signal: options.signal,
    });
  }
  if (response.status === 404) {
    const body = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, body);
    throwFromErrorBody(response.status, body, "Failed to load Strategy Lab catalog.");
  }
  const data = await parseJsonOrThrow<unknown>(response, "Failed to load Strategy Lab catalog.");
  const catalog = parseStrategyRdCatalogResponse(data);
  if (catalog.sessions?.sessions?.length) return catalog;

  // /concepts is picker-ready but does not include sessions. Those live on /presets.
  const presetsResponse = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/presets`, {
    signal: options.signal,
  });
  if (!presetsResponse.ok) return catalog;
  const presetsData = await parseJsonOrThrow<unknown>(
    presetsResponse,
    "Failed to load Strategy Lab sessions."
  );
  const sessions = parseStrategyRdCatalogResponse(presetsData).sessions;
  if (!sessions?.sessions?.length) return catalog;
  return { ...catalog, sessions };
}

/** What can be a condition in the "Condition turns true" setup. */
export async function listStrategyRdConditionChoices(
  signal?: AbortSignal
): Promise<StrategyRdConditionChoices> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/setups/condition-choices`, {
    signal,
  });
  const data = await parseJsonOrThrow<unknown>(response, "Failed to load setup conditions.");
  return parseStrategyRdConditionChoices(data);
}

function parseInstrumentRow(raw: unknown): StrategyRdInstrument | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const instrumentId = asString(rec.instrument_id).trim();
  const displaySymbol = asString(rec.display_symbol).trim();
  if (!instrumentId && !displaySymbol) return null;
  return {
    instrument_id: instrumentId || displaySymbol,
    display_symbol: displaySymbol || instrumentId,
    display_name: asOptionalString(rec.display_name),
    asset_class: asOptionalString(rec.asset_class),
    market_data_broker: asOptionalString(rec.market_data_broker),
    market_data_broker_display: asOptionalString(rec.market_data_broker_display),
    md_source: asOptionalString(rec.md_source),
    source_hidden: rec.source_hidden === true,
    from_overlay: rec.from_overlay === true,
  };
}

export function parseStrategyRdInstrumentsResponse(data: unknown): StrategyRdInstrumentsResponse {
  const root = asRecord(data) ?? {};
  const rawList = Array.isArray(root.instruments)
    ? root.instruments
    : Array.isArray(data)
      ? data
      : [];
  const instruments = rawList
    .map(parseInstrumentRow)
    .filter((row): row is StrategyRdInstrument => row != null);
  return {
    instruments,
    count: asNumber(root.count, instruments.length),
    liquid_eligible: root.liquid_eligible === true,
  };
}

export async function listStrategyRdInstruments(
  signal?: AbortSignal
): Promise<StrategyRdInstrumentsResponse> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/instruments`, { signal });
  if (response.status === 404) {
    const body = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, body);
    throwFromErrorBody(response.status, body, "Failed to load Strategy Lab instruments.");
  }
  const data = await parseJsonOrThrow<unknown>(
    response,
    "Failed to load Strategy Lab instruments."
  );
  return parseStrategyRdInstrumentsResponse(data);
}

/** @deprecated Prefer listStrategyRdCatalog — kept for callers that only need setup ids. */
export async function listStrategyRdPresets(
  signal?: AbortSignal
): Promise<StrategyRdPreset[]> {
  const catalog = await listStrategyRdCatalog(signal);
  if (catalog.presets.length > 0) return catalog.presets;
  return catalog.concepts
    .filter((c) => c.role === "setup")
    .map((c) => ({ id: c.id, name: c.name, description: "" }));
}

function applyMarketDataPins(
  target: Record<string, unknown>,
  body: { market_data_broker?: string | null; md_source?: string | null }
): void {
  const broker = body.market_data_broker?.trim();
  const source = body.md_source?.trim();
  if (broker) target.market_data_broker = broker;
  if (source) target.md_source = source;
}

function compactIdentityBody(
  body: StrategyRdResolveRequest | StrategyRdScanRequest
): StrategyRdResolveRequest | StrategyRdScanRequest {
  const next = { ...body } as StrategyRdResolveRequest & StrategyRdScanRequest;
  const resolvedSymbol = next.resolved_symbol?.trim();
  if (resolvedSymbol) next.resolved_symbol = resolvedSymbol;
  else delete next.resolved_symbol;
  const assetClass = next.asset_class?.trim();
  if (assetClass) next.asset_class = assetClass;
  else delete next.asset_class;

  const broker = next.market_data_broker?.trim();
  if (broker) next.market_data_broker = broker;
  else delete next.market_data_broker;
  const mdSource = (next as StrategyRdScanRequest).md_source?.trim();
  if (mdSource) (next as StrategyRdScanRequest).md_source = mdSource;
  else delete (next as StrategyRdScanRequest).md_source;

  if ("setup" in next || "pattern_id" in next || "setup_id" in next) {
    const scan = next as StrategyRdScanRequest;
    const nested = compactNestedComposerFields({
      setup: scan.setup,
      confluences: scan.confluences,
      entry: scan.entry,
      setupCandleType: scan.candle_type,
    });
    if (nested.setup) scan.setup = nested.setup;
    else delete scan.setup;
    if (nested.setup_id) {
      scan.setup_id = nested.setup_id;
      scan.pattern_id = nested.pattern_id || nested.setup_id;
    } else {
      const setupId = (scan.setup_id || scan.pattern_id || "").trim();
      if (setupId) {
        scan.setup_id = setupId;
        scan.pattern_id = setupId;
      } else {
        delete scan.setup_id;
        delete scan.pattern_id;
      }
    }
    if (nested.confluences) {
      scan.confluences = nested.confluences;
    } else if (Array.isArray(scan.confluences)) {
      const slots = strategyRdConfluenceSlotsForRequest(
        scan.confluences,
        scan.candle_type || ""
      );
      if (slots.length === 0) delete scan.confluences;
      else scan.confluences = slots;
    } else {
      delete scan.confluences;
    }
    if (nested.entry) {
      scan.entry = nested.entry;
      if (nested.entry_mode) scan.entry_mode = nested.entry_mode;
    } else {
      delete scan.entry;
    }
    const confluenceIds = strategyRdConfluenceIds(
      scan.confluences as Array<string | StrategyRdComposerConfluenceSlot> | undefined
    );
    const srScopes = strategyRdSrScopesForRequest(scan.sr_scopes, confluenceIds);
    if (srScopes) scan.sr_scopes = srScopes;
    else delete scan.sr_scopes;
    if (Array.isArray(scan.session_ids)) {
      scan.session_ids = scan.session_ids
        .map((s) => String(s).trim().toLowerCase())
        .filter(Boolean);
      if (scan.session_ids.length === 0) {
        delete scan.session_ids;
      } else {
        delete scan.session_id;
      }
    } else {
      delete scan.session_ids;
    }
    const sessionId = scan.session_id?.trim();
    if (sessionId && !scan.session_ids) scan.session_id = sessionId;
    else delete scan.session_id;
    const entryMode = scan.entry_mode?.trim() || nested.entry_mode;
    if (entryMode) scan.entry_mode = entryMode;
    else delete scan.entry_mode;
    const sidePolicy = scan.side_policy?.trim();
    if (sidePolicy) scan.side_policy = sidePolicy;
    else delete scan.side_policy;
    if (scan.template == null) delete scan.template;
  }
  return next;
}

/** Exported for unit tests — Compacts Concepts scan body fields. */
export function compactStrategyRdScanRequest(
  body: StrategyRdScanRequest
): StrategyRdScanRequest {
  return compactIdentityBody(body) as StrategyRdScanRequest;
}

export async function resolveStrategyRd(
  body: StrategyRdResolveRequest,
  signal?: AbortSignal
): Promise<StrategyRdResolveResponse> {
  const requestBody = compactIdentityBody(body);
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/resolve`, {
    method: "POST",
    body: JSON.stringify(requestBody),
    signal,
  });

  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, "Could not confirm this symbol.");
  }

  const payload = await parseJsonOrThrow<unknown>(response, "Could not confirm this symbol.");
  const row = asRecord(payload) ?? {};
  return {
    status: asString(row.status).trim() || "ready",
    symbol: asString(row.symbol).trim(),
    resolved_symbol: asString(row.resolved_symbol).trim() || asString(row.symbol).trim(),
    trade_symbol: asOptionalString(row.trade_symbol),
    asset_class: asOptionalString(row.asset_class),
    market_data_broker: asOptionalString(row.market_data_broker),
    market_data_broker_display: asOptionalString(row.market_data_broker_display),
    md_source: asOptionalString(row.md_source),
    md_listing_key: asOptionalString(row.md_listing_key),
    chart_available: row.chart_available !== false,
    market_data_sources: row.market_data_sources,
  };
}

/**
 * Finished scans kept in memory for a few minutes: coming back to the Lab (or re-opening the
 * same backtest) re-runs the identical scan, which took seconds of candles + detection.
 */
const SCAN_CACHE_TTL_MS = 10 * 60_000;
const SCAN_CACHE_MAX = 3;
const scanCache = new Map<string, { at: number; result: StrategyRdScanResponse }>();

/** Drop cached scans (e.g. after the data for a market changed). */
export function clearStrategyRdScanCache(): void {
  scanCache.clear();
}

export async function scanStrategyRd(
  body: StrategyRdScanRequest,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdScanResponse> {
  const requestBody = compactIdentityBody(body) as StrategyRdScanRequest;
  const cacheKey = JSON.stringify(requestBody);
  const cached = scanCache.get(cacheKey);
  if (cached && Date.now() - cached.at < SCAN_CACHE_TTL_MS) {
    scanCache.delete(cacheKey);
    scanCache.set(cacheKey, cached); // most recent last
    return cached.result;
  }
  const result = await scanStrategyRdUncached(requestBody, signal, onProgress);
  scanCache.set(cacheKey, { at: Date.now(), result });
  while (scanCache.size > SCAN_CACHE_MAX) {
    const oldest = scanCache.keys().next().value;
    if (oldest === undefined) break;
    scanCache.delete(oldest);
  }
  return result;
}

async function scanStrategyRdUncached(
  requestBody: StrategyRdScanRequest,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdScanResponse> {
  // One job: keep polling through backfilling — BE resumes detect when the MD strip completes.
  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/scan`,
    requestBody,
    {
      signal,
      maxWaitMs: 300_000,
      onProgress,
      stopOn: ["ready", "failed"],
      fallbackErrorMessage: "Failed to scan pattern.",
    }
  );

  const status = asString(payload.status).trim().toLowerCase();
  if (status === "ready") {
    return parseStrategyRdScanResponse(payload, "ready");
  }
  throwStrategyRdJobFailed(payload, "Failed to scan pattern.");
}

/** Exported for unit tests — preview-concepts body (Entry shorthand always included). */
export function compactPreviewConceptsBody(
  body: StrategyRdPreviewConceptsRequest
): Record<string, unknown> {
  const nested = compactNestedComposerFields({
    setup: body.setup,
    confluences: body.confluences,
    entry: body.entry,
    setupCandleType: body.candle_type,
  });
  const setupId =
    nested.setup_id || (body.setup_id || body.pattern_id || "").trim();
  const payload: Record<string, unknown> = {
    symbol: body.symbol.trim(),
    ...(body.broker_account_code?.trim() ? { broker_account_code: body.broker_account_code.trim() } : {}),
  };
  if (nested.setup) payload.setup = nested.setup;
  if (setupId) {
    payload.setup_id = setupId;
    payload.pattern_id = setupId;
  }
  if (nested.confluences) {
    payload.confluences = nested.confluences;
  } else if (Array.isArray(body.confluences) && body.confluences.length > 0) {
    // Always promote legacy string[] to nested slots with setup TF (never send flat ids).
    const slots = strategyRdConfluenceSlotsForRequest(body.confluences, body.candle_type || "");
    if (slots.length > 0) payload.confluences = slots;
  }
  const confluenceIds = strategyRdConfluenceIds(
    (payload.confluences as Array<string | StrategyRdComposerConfluenceSlot>) || body.confluences
  );
  const srScopes = strategyRdSrScopesForRequest(body.sr_scopes, confluenceIds);
  if (srScopes) payload.sr_scopes = srScopes;
  if (Array.isArray(body.session_ids) && body.session_ids.length > 0) {
    payload.session_ids = body.session_ids
      .map((s) => String(s).trim().toLowerCase())
      .filter(Boolean);
  } else if (body.session_id?.trim()) {
    payload.session_id = body.session_id.trim();
  }
  if (body.candle_type?.trim()) payload.candle_type = body.candle_type.trim();
  if (body.lookback_days != null && Number.isFinite(body.lookback_days)) {
    payload.lookback_days = body.lookback_days;
  }
  if (body.start_date?.trim()) payload.start_date = body.start_date.trim();
  if (body.end_date?.trim()) payload.end_date = body.end_date.trim();
  if (body.asset_class?.trim()) payload.asset_class = body.asset_class.trim();
  if (body.resolved_symbol?.trim()) payload.resolved_symbol = body.resolved_symbol.trim();
  applyMarketDataPins(payload, body);
  if (body.md_listing_key?.trim()) payload.md_listing_key = body.md_listing_key.trim();
  if (nested.entry) payload.entry = nested.entry;
  const templateEntry = body.template?.entry;
  const entryMode =
    nested.entry_mode ||
    body.entry_mode?.trim() ||
    (typeof templateEntry?.mode === "string" ? templateEntry.mode.trim() : "");
  const sidePolicy =
    body.side_policy?.trim() ||
    (typeof templateEntry?.side_policy === "string" ? templateEntry.side_policy.trim() : "");
  const maxWait =
    strategyRdMaxWaitForWire(nested.entry?.max_wait) ||
    strategyRdMaxWaitForWire(templateEntry?.max_wait);
  if (entryMode) payload.entry_mode = entryMode;
  if (sidePolicy) payload.side_policy = sidePolicy;
  // Only echo template.entry when caller supplied a template (strip stop/target). A custom
  // entry (mode "rule") needs its rule, and an entry filter its filter — without them the
  // server rejects the preview.
  const entryRule = entryMode === "rule" ? templateEntry?.rule ?? null : null;
  const entryFilter = templateEntry?.filter ?? null;
  if (body.template?.entry && (entryMode || sidePolicy || maxWait)) {
    payload.template = {
      entry: {
        ...(entryMode ? { mode: entryMode } : {}),
        ...(sidePolicy ? { side_policy: sidePolicy } : {}),
        ...(maxWait ? { max_wait: maxWait } : {}),
        ...(entryRule ? { rule: entryRule } : {}),
        ...(entryFilter ? { filter: entryFilter } : {}),
      },
    };
  }
  if (Array.isArray(body.candles) && body.candles.length > 0) {
    payload.candles = body.candles;
  }
  return payload;
}

/**
 * Live composer preview: sync 200 when candles provided; else async 202 job poll.
 * Without candles, poll the **same** job through `backfilling` until `ready` / `failed`
 * (BE auto-resumes after OHLC strip). Cache returned candles for later sync Previews.
 */
export async function previewStrategyRdConcepts(
  body: StrategyRdPreviewConceptsRequest,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdPreviewConceptsResponse> {
  const requestBody = compactPreviewConceptsBody(body);
  const hasCandles = Array.isArray(requestBody.candles) && (requestBody.candles as unknown[]).length > 0;

  if (hasCandles) {
    const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/preview-concepts`, {
      method: "POST",
      body: JSON.stringify(requestBody),
      signal,
    });
    if (response.status === 404) {
      const errBody = await response.json().catch(() => ({}));
      throwIfStrategyRdDisabled(response, errBody);
      throwFromErrorBody(response.status, errBody, "Could not preview concepts.");
    }
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throwFromErrorBody(response.status, errBody, "Could not preview concepts.");
    }
    const payload = await parseJsonOrThrow<unknown>(response, "Could not preview concepts.");
    return parseStrategyRdScanResponse(payload, "ready");
  }

  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/preview-concepts`,
    requestBody,
    {
      signal,
      maxWaitMs: 300_000,
      onProgress,
      stopOn: ["ready", "failed"],
      fallbackErrorMessage: "Could not preview concepts.",
    }
  );

  const status = asString(payload.status).trim().toLowerCase();
  if (status === "ready") {
    return parseStrategyRdScanResponse(payload, "ready");
  }
  throwStrategyRdJobFailed(payload, "Could not preview concepts.");
}

/** Sync: re-attach Rules entry paths to prior scan matches (no job poll). */
export async function previewStrategyRdPath(
  body: StrategyRdPreviewPathRequest,
  signal?: AbortSignal
): Promise<StrategyRdPreviewPathResponse> {
  const payloadBody: Record<string, unknown> = { matches: body.matches };
  if (body.scan_job_id && body.candles.length === 0) {
    payloadBody.scan_job_id = body.scan_job_id;
  } else {
    payloadBody.candles = body.candles;
  }
  if (body.template) {
    payloadBody.template = body.template;
  } else {
    if (body.entry_mode?.trim()) payloadBody.entry_mode = body.entry_mode.trim();
    if (body.side_policy?.trim()) payloadBody.side_policy = body.side_policy.trim();
  }

  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/preview-path`, {
    method: "POST",
    body: JSON.stringify(payloadBody),
    signal,
  });

  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, "Could not preview entry path.");
  }

  const payload = await parseJsonOrThrow<unknown>(response, "Could not preview entry path.");
  const row = asRecord(payload) ?? {};
  const matchesRaw = Array.isArray(row.matches) ? row.matches : [];
  return {
    status: asString(row.status).trim() || "ready",
    matches: matchesRaw
      .map(parseMatch)
      .filter((m): m is StrategyRdMatch => m != null),
    path_preview: row.path_preview !== false,
    template: row.template ? parseRuleTemplate(row.template) : null,
  };
}

export type StrategyRdMatchWindowRequest = {
  symbol: string;
  broker_account_code?: string | null;
  bar_time: string;
  setup_candle_type: string;
  candle_type: string;
  asset_class?: string | null;
  resolved_symbol?: string | null;
  market_data_broker?: string | null;
  md_source?: string | null;
};

export type StrategyRdMatchWindowResponse = {
  chart: StrategyRdChartParams | null;
  pine_multi_tf_supported: boolean;
  candles?: StrategyRdOhlcBar[];
  /** The Lab candles endpoint for this window (§11.5) — `fetchStrategyRdCandles({url})`. */
  candles_url?: string | null;
};

/** Fine-TF pad around one match for multi-TF chart dive. */
export async function fetchStrategyRdMatchWindow(
  body: StrategyRdMatchWindowRequest,
  signal?: AbortSignal
): Promise<StrategyRdMatchWindowResponse> {
  const payload: Record<string, unknown> = {
    symbol: body.symbol.trim(),
    ...(body.broker_account_code?.trim() ? { broker_account_code: body.broker_account_code.trim() } : {}),
    bar_time: body.bar_time.trim(),
    setup_candle_type: body.setup_candle_type.trim(),
    candle_type: body.candle_type.trim(),
  };
  if (body.asset_class?.trim()) payload.asset_class = body.asset_class.trim();
  if (body.resolved_symbol?.trim()) payload.resolved_symbol = body.resolved_symbol.trim();
  applyMarketDataPins(payload, body);

  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/match-window`, {
    method: "POST",
    body: JSON.stringify(payload),
    signal,
  });
  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, "Could not load match window.");
  }
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throwFromErrorBody(response.status, errBody, "Could not load match window.");
  }
  const raw = await parseJsonOrThrow<unknown>(response, "Could not load match window.");
  const row = asRecord(raw) ?? {};
  return {
    chart: row.chart ? parseChartParams(row.chart) : null,
    pine_multi_tf_supported: row.pine_multi_tf_supported === true,
    candles: parseOhlcBars(row.candles),
    candles_url: asOptionalString(row.candles_url),
  };
}

export type StrategyRdCandlesQuery = {
  symbol: string;
  candle_type: string;
  /** ISO, with `end_date` — or `lookback_days` instead. */
  start_date?: string;
  end_date?: string;
  lookback_days?: number;
  asset_class?: string | null;
  resolved_symbol?: string | null;
  market_data_broker?: string | null;
};

export type StrategyRdCandlesResult = {
  candles: StrategyRdOhlcBar[];
  /** History for part of the window wasn't available (after any backfill). */
  partial: boolean;
};

const CANDLES_STRIP_MAX_WAIT_MS = 180_000;

function strategyRdCandlesPath(query: StrategyRdCandlesQuery | { url: string }): string {
  if ("url" in query) return strategyRdApiPathFromPollUrl(query.url);
  const q = new URLSearchParams({ symbol: query.symbol.trim(), candle_type: query.candle_type.trim() });
  if (query.start_date && query.end_date) {
    q.set("start_date", query.start_date);
    q.set("end_date", query.end_date);
  } else if (query.lookback_days) {
    q.set("lookback_days", String(Math.max(1, Math.floor(query.lookback_days))));
  }
  if (query.asset_class?.trim()) q.set("asset_class", query.asset_class.trim());
  if (query.resolved_symbol?.trim()) q.set("resolved_symbol", query.resolved_symbol.trim());
  if (query.market_data_broker?.trim()) q.set("market_data_broker", query.market_data_broker.trim());
  return `${STRATEGY_RD_API_BASE}/candles?${q.toString()}`;
}

/**
 * Lab chart candles from the central lane — no broker account (§11.5). A cold window answers
 * 202 + `poll_url`: `onPartial` gets the candles already there, the strip is polled until it
 * finishes, then the window is read once more. The journal keeps `/trades/execution-candles`.
 */
export async function fetchStrategyRdCandles(
  query: StrategyRdCandlesQuery | { url: string },
  opts: {
    signal?: AbortSignal;
    onPartial?: (candles: StrategyRdOhlcBar[], progress: { done: number; total: number } | null) => void;
  } = {}
): Promise<StrategyRdCandlesResult> {
  const path = strategyRdCandlesPath(query);
  const read = async () => {
    const response = await authenticatedFetch(path, { signal: opts.signal });
    const body = asRecord(await response.json().catch(() => ({}))) ?? {};
    if (!response.ok && response.status !== 202) {
      throwIfStrategyRdDisabled(response, body);
      throwFromErrorBody(response.status, body, "Could not load the chart.");
    }
    return { status: response.status, body };
  };

  const first = await read();
  if (first.status !== 202) {
    return { candles: parseOhlcBars(first.body.candles), partial: first.body.partial_coverage === true };
  }
  const partial = parseOhlcBars(first.body.candles);
  opts.onPartial?.(partial, null);
  const pollUrl = asString(first.body.poll_url).trim();
  if (pollUrl) {
    // Same strip poll as the journal's execution-candles backfill.
    await pollOhlcStripUntilDone(pollUrl, opts.signal, undefined, CANDLES_STRIP_MAX_WAIT_MS, (progress) =>
      opts.onPartial?.(
        partial,
        progress ? { done: progress.done_chunks, total: progress.total_chunks } : null
      )
    );
  }
  const second = await read();
  return {
    candles: parseOhlcBars(second.body.candles),
    partial: second.status === 202 || second.body.partial_coverage === true,
  };
}

function parseOffset(raw: unknown): StrategyRdRuleTemplate["stop"]["offset"] {
  const row = asRecord(raw) ?? {};
  const kindRaw = asString(row.kind).trim() || "none";
  const kind: StrategyRdOffsetKind =
    kindRaw === "beyond_bar" ||
    kindRaw === "beyond_level" ||
    kindRaw === "pct_price" ||
    kindRaw === "atr_mult" ||
    kindRaw === "pips" ||
    kindRaw === "ticks"
      ? kindRaw
      : "none";
  return { kind, value: asNumber(row.value, 0) };
}

function parseEntryMode(raw: unknown, hasRule = false): StrategyRdEntryMode {
  const mode = asString(raw).trim();
  // A custom entry keeps its mode — as "signal_close" its rule is ignored and no trade is drawn.
  if (mode === "rule" && hasRule) return "rule";
  if (
    mode === "signal_open" ||
    mode === "next_open" ||
    mode === "break_close" ||
    mode === "retest_then_close" ||
    mode === "rejection" ||
    mode === "limit_at_level" ||
    mode === "signal_close"
  ) {
    return mode;
  }
  return "signal_close";
}

function parseStopAnchor(raw: unknown): StrategyRdStopAnchor {
  const anchor = asString(raw).trim();
  if (
    anchor === "signal_close" ||
    anchor === "level_extreme" ||
    anchor === "prior_day" ||
    anchor === "prior_week" ||
    anchor === "prior_month" ||
    anchor === "swing" ||
    anchor === "prior_day_low" ||
    anchor === "prior_day_high" ||
    anchor === "prior_week_low" ||
    anchor === "prior_week_high" ||
    anchor === "prior_month_low" ||
    anchor === "prior_month_high" ||
    anchor === "swing_low" ||
    anchor === "swing_high"
  ) {
    return anchor;
  }
  if (anchor === "signal_extreme") return "signal_extreme";
  return anchor || "signal_extreme";
}

function parseTargetMode(raw: unknown): StrategyRdTargetMode {
  const mode = asString(raw).trim() || "r_multiple";
  if (
    mode === "atr_mult" ||
    mode === "pct_price" ||
    mode === "pips" ||
    mode === "ticks" ||
    mode === "prior_day" ||
    mode === "prior_week" ||
    mode === "prior_month" ||
    mode === "swing" ||
    mode === "prior_day_low" ||
    mode === "prior_day_high" ||
    mode === "prior_week_low" ||
    mode === "prior_week_high" ||
    mode === "prior_month_low" ||
    mode === "prior_month_high" ||
    mode === "swing_low" ||
    mode === "swing_high" ||
    mode === "level_extreme" ||
    mode === "r_multiple"
  ) {
    return mode;
  }
  return mode;
}

/** Coach Confirm / hypothesis payloads — full merged template from the turn. */
export function parseStrategyRdRuleTemplate(raw: unknown): StrategyRdRuleTemplate {
  return parseRuleTemplate(raw);
}

function parseRuleTemplate(raw: unknown): StrategyRdRuleTemplate {
  const row = asRecord(raw) ?? {};
  const entry = asRecord(row.entry) ?? {};
  const stop = asRecord(row.stop) ?? {};
  const target = asRecord(row.target) ?? {};
  const targetMode = parseTargetMode(target.mode);
  const pip = asNumber(row.pip_size, Number.NaN);
  const tick = asNumber(row.tick_size, Number.NaN);
  const maxWait = strategyRdMaxWaitForWire(
    typeof entry.max_wait === "string"
      ? { unit: entry.max_wait }
      : (asRecord(entry.max_wait) as { value?: number | null; unit?: string | null } | null)
  );
  return {
    entry: {
      mode: parseEntryMode(entry.mode, asRecord(entry.rule) != null),
      side_policy:
        asString(entry.side_policy).trim() === "long_only" ||
        asString(entry.side_policy).trim() === "short_only"
          ? asString(entry.side_policy).trim()
          : asString(entry.side_policy).trim() === "both"
            ? "both"
            : undefined,
      ...(maxWait ? { max_wait: maxWait } : {}),
      ...(asRecord(entry.rule)
        ? { rule: asRecord(entry.rule) as unknown as StrategyRdEntryRuleSpec }
        : {}),
      ...(asRecord(entry.filter) ? { filter: asRecord(entry.filter) } : {}),
      ...(asString(entry.fill).trim() === "next_open" ? { fill: "next_open" as const } : {}),
    },
    stop: {
      anchor: parseStopAnchor(stop.anchor),
      offset: parseOffset(stop.offset),
      ...(parseDistanceBound(stop.min_distance) ? { min_distance: parseDistanceBound(stop.min_distance) } : {}),
      ...(parseDistanceBound(stop.max_distance) ? { max_distance: parseDistanceBound(stop.max_distance) } : {}),
      ...(asRecord(stop.rule) ? { rule: asRecord(stop.rule) } : {}),
    },
    target: {
      mode: targetMode,
      value: asNumber(target.value, 2),
      ...(asRecord(target.rule) ? { rule: asRecord(target.rule) } : {}),
      ...(asNumber(target.min_r, 0) > 0 ? { min_r: asNumber(target.min_r, 0) } : {}),
    },
    atr_length: asNumber(row.atr_length, 14),
    pip_size: Number.isFinite(pip) && pip > 0 ? pip : null,
    tick_size: Number.isFinite(tick) && tick > 0 ? tick : null,
    ...(parseIntrabar(row.intrabar) ? { intrabar: parseIntrabar(row.intrabar) } : {}),
    ...(asRecord(row.manage)
      ? { manage: asRecord(row.manage) as unknown as StrategyRdManageRule }
      : {}),
  };
}

function parseDistanceBound(raw: unknown): StrategyRdStopDistanceBound | null {
  const row = asRecord(raw);
  const kind = asString(row?.kind).trim();
  const value = parseOptionalFinite(row?.value);
  if (!row || value == null || !(value > 0)) return null;
  return kind === "pct_price" || kind === "atr_mult" || kind === "pips" || kind === "ticks" || kind === "price"
    ? { kind, value }
    : null;
}

function parseIntrabar(raw: unknown): StrategyRdIntrabarMode | null {
  const v = asString(raw).trim();
  return v === "bar_path" || v === "bar_magnifier" || v === "stop_first" ? v : null;
}

function parseScanContext(raw: unknown): StrategyRdScanContext {
  const row = asRecord(raw) ?? {};
  const confluences = Array.isArray(row.confluences)
    ? row.confluences.map((c) => asString(c).trim()).filter(Boolean)
    : null;
  const sessionIds = Array.isArray(row.session_ids)
    ? row.session_ids.map((c) => asString(c).trim().toLowerCase()).filter(Boolean)
    : null;
  return {
    symbol: asString(row.symbol).trim(),
    broker_account_code: asString(row.broker_account_code).trim(),
    pattern_id: asOptionalString(row.pattern_id),
    setup_id: asOptionalString(row.setup_id),
    confluences,
    sr_scopes: Array.isArray(row.sr_scopes)
      ? normalizeStrategyRdSrScopes(row.sr_scopes)
      : null,
    session_ids: sessionIds && sessionIds.length > 0 ? sessionIds : null,
    session_id: asOptionalString(row.session_id),
    candle_type: asOptionalString(row.candle_type),
    resolved_symbol: asOptionalString(row.resolved_symbol),
    asset_class: asOptionalString(row.asset_class),
    lookback_days:
      typeof row.lookback_days === "number" && Number.isFinite(row.lookback_days)
        ? row.lookback_days
        : null,
    start_date: asOptionalString(row.start_date),
    end_date: asOptionalString(row.end_date),
    market_data_broker: asOptionalString(row.market_data_broker),
    md_source: asOptionalString(row.md_source),
    composer: asRecord(row.composer),
    optional_min: parseOptionalFinite(row.optional_min),
  };
}

function parseHypothesis(raw: unknown): StrategyRdHypothesis | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = asString(row.id).trim();
  if (!id) return null;
  const hint = asRecord(row.report_strategy_hint);
  return {
    id,
    name: asString(row.name).trim() || id,
    created_at: asOptionalString(row.created_at),
    updated_at: asOptionalString(row.updated_at),
    scan_context: parseScanContext(row.scan_context),
    template: parseRuleTemplate(row.template),
    report_strategy_hint: hint
      ? {
          report_strategy_id: asOptionalString(hint.report_strategy_id),
          deep_link: asOptionalString(hint.deep_link),
        }
      : null,
    last_apply: parseLastApply(row.last_apply),
    last_robustness: parseStrategyRdRobustness(row.last_robustness),
    last_markets: row.last_markets ?? null,
    parent_id: asOptionalString(row.parent_id),
    root_id: asOptionalString(row.root_id),
    version: parseOptionalFinite(row.version),
    created_by: asOptionalString(row.created_by),
  };
}

export function parseStrategyRdHeadline(raw: unknown): StrategyRdHeadlineStats {
  const row = asRecord(raw) ?? {};
  const keys = [
    "closed_count",
    "win_rate",
    "expectancy_r",
    "sum_r",
    "profit_factor_r",
    "max_drawdown_r",
    "sum_pnl",
    "return_on_starting_equity_pct",
    "profit_factor",
    "max_drawdown_pct",
  ] as const;
  const out: StrategyRdHeadlineStats = {};
  for (const k of keys) {
    const v = parseOptionalFinite(row[k]);
    if (v != null) out[k] = v;
  }
  return out;
}

export function parseStrategyRdHoldoutSplit(raw: unknown): StrategyRdHoldoutSplit | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    holdout_pct: parseOptionalFinite(row.holdout_pct),
    cut_time: asOptionalString(row.cut_time),
    first_time: asOptionalString(row.first_time),
    last_time: asOptionalString(row.last_time),
    tuning: parseStrategyRdHeadline(row.tuning),
    unseen: parseStrategyRdHeadline(row.unseen),
    verdict: asString(row.verdict).trim() || "too_few_trades",
  };
}

export function parseStrategyRdHalves(raw: unknown): StrategyRdHalves | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    cut_time: asOptionalString(row.cut_time),
    first: parseStrategyRdHeadline(row.first),
    second: parseStrategyRdHeadline(row.second),
    verdict: asString(row.verdict).trim() || "too_few_trades",
  };
}

export function parseStrategyRdConcentration(raw: unknown): StrategyRdConcentration | null {
  const row = asRecord(raw);
  if (!row) return null;
  const verdict = asString(row.verdict).trim();
  if (!verdict) return null;
  return {
    verdict,
    dropped: parseOptionalFinite(row.dropped),
    best_share_of_sum_r: parseOptionalFinite(row.best_share_of_sum_r),
    expectancy_r: parseOptionalFinite(row.expectancy_r),
    expectancy_r_without_best: parseOptionalFinite(row.expectancy_r_without_best),
    closed_count: parseOptionalFinite(row.closed_count),
  };
}

export function parseStrategyRdMonthBuckets(raw: unknown): StrategyRdMonthBucket[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out = raw.flatMap((item) => {
    const row = asRecord(item);
    const month = asString(row?.month).trim();
    if (!row || !month) return [];
    return [
      {
        month,
        closed_count: parseOptionalFinite(row.closed_count),
        expectancy_r: parseOptionalFinite(row.expectancy_r),
        sum_r: parseOptionalFinite(row.sum_r),
        sum_pnl: parseOptionalFinite(row.sum_pnl),
      } satisfies StrategyRdMonthBucket,
    ];
  });
  return out.length > 0 ? out : null;
}

export function parseStrategyRdStability(raw: unknown): StrategyRdStability | null {
  const row = asRecord(raw);
  if (!row) return null;
  const halves = parseStrategyRdHalves(row.halves);
  const concentration = parseStrategyRdConcentration(row.concentration);
  const months = parseStrategyRdMonthBuckets(row.months);
  if (!halves && !concentration && !months) return null;
  return { halves, concentration, months };
}

export function parseStrategyRdCostStress(raw: unknown): StrategyRdCostStress | null {
  const row = asRecord(raw);
  if (!row) return null;
  const verdict = asString(row.verdict).trim();
  if (!verdict) return null;
  const variants: StrategyRdCostStressVariant[] = Array.isArray(row.variants)
    ? row.variants.flatMap((item) => {
        const v = asRecord(item);
        const label = asString(v?.label).trim();
        if (!v || !label) return [];
        return [
          {
            label,
            return_pct: parseOptionalFinite(v.return_pct),
            net_pnl: parseOptionalFinite(v.net_pnl),
            trades: parseOptionalFinite(v.trades),
            win_rate: parseOptionalFinite(v.win_rate),
          },
        ];
      })
    : [];
  return { verdict, variants };
}

/** Card `robustness.cost_stress` is the verdict string; the job stores the object. */
export function parseStrategyRdCostStressVerdict(raw: unknown): string | null {
  if (typeof raw === "string") {
    const s = raw.trim();
    return s || null;
  }
  return asOptionalString(asRecord(raw)?.verdict) ?? null;
}

export function parseStrategyRdSweep(raw: unknown): StrategyRdSweepTable | null {
  const row = asRecord(raw);
  if (!row || !Array.isArray(row.combinations)) return null;
  return {
    metric: asString(row.metric).trim() || "expectancy_r",
    holdout_pct: parseOptionalFinite(row.holdout_pct),
    combinations: row.combinations.flatMap((item) => {
      const c = asRecord(item);
      if (!c) return [];
      return [
        {
          params: asRecord(c.params) ?? {},
          stats: parseStrategyRdHeadline(c.stats),
          split: parseStrategyRdHoldoutSplit(c.split),
        },
      ];
    }),
    best: asRecord(row.best) ?? {},
    best_verdict: asOptionalString(row.best_verdict),
  };
}

function parseOptionalFinite(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function parseExcursionUnit(raw: unknown): StrategyRdExcursionUnit | null {
  const u = asString(raw).trim().toLowerCase();
  if (
    u === "atr" ||
    u === "r" ||
    u === "pct_price" ||
    u === "pips" ||
    u === "ticks" ||
    u === "price"
  ) {
    return u;
  }
  return null;
}

function parseExcursion(raw: unknown): StrategyRdExcursion | null {
  const row = asRecord(raw);
  if (!row) return null;
  const unit = parseExcursionUnit(row.unit) ?? "price";
  return {
    unit,
    mfe: parseOptionalFinite(row.mfe),
    mae: parseOptionalFinite(row.mae),
    mfe_price: parseOptionalFinite(row.mfe_price),
    mae_price: parseOptionalFinite(row.mae_price),
  };
}

/** Coach Confirm — full merged sizing from the turn (prefer over sizing_patch). */
export function parseStrategyRdSizing(raw: unknown): StrategyRdSizing | null {
  return parseSizing(raw);
}

function parseSizing(raw: unknown): StrategyRdSizing | null {
  const row = asRecord(raw);
  if (!row) return null;
  const modeRaw = asString(row.mode).trim().toLowerCase();
  if (modeRaw !== "risk_pct" && modeRaw !== "fixed_qty") {
    return null;
  }
  const value = parseOptionalFinite(row.value);
  if (value == null || value <= 0) return null;
  const leverage = parseOptionalFinite(row.leverage);
  const positionMode = asString(row.position_mode).trim();
  const commission = parseOptionalFinite(row.commission_pct);
  const slippage = parseOptionalFinite(row.slippage_ticks);
  const spreadPct = parseOptionalFinite(row.spread_pct);
  // The spread as a price ($0.30): TES sets it from "spread $0.30" — dropping it lost the cost.
  const spread = parseOptionalFinite(row.spread);
  const swapLong = parseOptionalFinite(row.swap_long_pct);
  const swapShort = parseOptionalFinite(row.swap_short_pct);
  return {
    mode: modeRaw,
    value,
    ...(leverage != null && leverage > 0 ? { leverage } : {}),
    ...(positionMode === "shared" ||
    positionMode === "independent" ||
    positionMode === "combined" ||
    positionMode === "one_at_a_time"
      ? { position_mode: positionMode }
      : {}),
    ...(commission != null ? { commission_pct: commission } : {}),
    ...(slippage != null ? { slippage_ticks: slippage } : {}),
    ...(spread != null && spread > 0 ? { spread } : {}),
    ...(spreadPct != null ? { spread_pct: spreadPct } : {}),
    ...(swapLong != null ? { swap_long_pct: swapLong } : {}),
    ...(swapShort != null ? { swap_short_pct: swapShort } : {}),
    ...(parseOptionalFinite(row.tick_size_used) != null
      ? { tick_size_used: parseOptionalFinite(row.tick_size_used) }
      : {}),
    ...(asRecord(row.limits)
      ? {
          limits: {
            max_trades_per_day: parseOptionalFinite(asRecord(row.limits)?.max_trades_per_day),
            max_losses_per_day: parseOptionalFinite(asRecord(row.limits)?.max_losses_per_day),
          },
        }
      : {}),
  };
}

/** Apply `warnings[]` / `overview.warnings` — kept in the order received. */
export function parseStrategyRdBacktestWarnings(raw: unknown): StrategyRdBacktestWarning[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.flatMap((item) => {
    const row = asRecord(item);
    const code = asString(row?.code).trim();
    const title = asString(row?.title).trim();
    if (!row || !code || !title) return [];
    const action = asRecord(row.action);
    const actionType = asString(action?.type).trim();
    return [
      {
        code,
        severity: asString(row.severity).trim().toLowerCase() || "info",
        title,
        message: asOptionalString(row.message),
        action:
          action && actionType
            ? {
                type: actionType,
                label: asString(action.label).trim() || actionType,
                field: asOptionalString(action.field),
              }
            : null,
      },
    ];
  });
}

function parseMarginCalls(raw: unknown): StrategyRdMarginCall[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: StrategyRdMarginCall[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) continue;
    out.push({
      bar_time: asOptionalString(row.bar_time),
      qty: parseOptionalFinite(row.qty),
      price: parseOptionalFinite(row.price),
      pnl: parseOptionalFinite(row.pnl),
    });
  }
  return out;
}

function parseMarginSummary(raw: unknown): StrategyRdMarginSummary | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    leverage: parseOptionalFinite(row.leverage),
    peak_margin: parseOptionalFinite(row.peak_margin),
    min_free_margin: parseOptionalFinite(row.min_free_margin),
    margin_calls: parseOptionalFinite(row.margin_calls),
  };
}

function parseCurrencyInfo(raw: unknown): StrategyRdCurrencyInfo | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    account: asString(row.account).trim().toUpperCase() || "USD",
    converted: row.converted === true,
    rate_pair: asOptionalString(row.rate_pair),
    reason: asOptionalString(row.reason),
  };
}

function parseParity(raw: unknown): StrategyRdParity | null {
  const row = asRecord(raw);
  if (!row) return null;
  const level = asString(row.level).trim();
  if (!level) return null;
  return {
    level,
    tolerance_bars: parseOptionalFinite(row.tolerance_bars),
    kind: asOptionalString(row.kind),
    source: asOptionalString(row.source),
    tv_symbol: asOptionalString(row.tv_symbol),
    reason: asOptionalString(row.reason),
  };
}

function parsePercentiles(raw: unknown): StrategyRdPercentiles | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    p5: parseOptionalFinite(row.p5),
    median: parseOptionalFinite(row.median),
    p95: parseOptionalFinite(row.p95),
  };
}

/** `hypothesis.last_robustness` or the robustness job result. */
export function parseStrategyRdRobustness(raw: unknown): StrategyRdRobustness | null {
  const row = asRecord(raw);
  if (!row) return null;
  const status = asString(row.status).trim().toLowerCase();
  if (!status) return null;
  const baseline = asRecord(row.baseline);
  const stability = asRecord(row.trade_stability);
  const calibration = asRecord(row.calibration);
  const error = asRecord(row.error);
  const fragile = Array.isArray(row.fragile_trades) ? row.fragile_trades : [];
  return {
    status,
    job_id: asOptionalString(row.job_id),
    applied_at: asOptionalString(row.applied_at),
    superseded: row.superseded === true,
    error: error
      ? { code: asOptionalString(error.code), message: asOptionalString(error.message) }
      : null,
    reason: asOptionalString(row.reason),
    verdict: asOptionalString(row.verdict),
    summary: asOptionalString(row.summary),
    runs: parseOptionalFinite(row.runs),
    baseline: baseline
      ? {
          trades: parseOptionalFinite(baseline.trades),
          net_pnl: parseOptionalFinite(baseline.net_pnl),
          return_pct: parseOptionalFinite(baseline.return_pct),
          win_rate: parseOptionalFinite(baseline.win_rate),
        }
      : null,
    return_pct: parsePercentiles(row.return_pct),
    trades: parsePercentiles(row.trades),
    win_rate: parsePercentiles(row.win_rate),
    trade_stability: stability
      ? {
          median: parseOptionalFinite(stability.median),
          p5: parseOptionalFinite(stability.p5),
          extra_trades_median: parseOptionalFinite(stability.extra_trades_median),
        }
      : null,
    fragile_trades: fragile.flatMap((item) => {
      const f = asRecord(item);
      const entryTime = asString(f?.entry_time).trim();
      if (!f || !entryTime) return [];
      return [
        {
          entry_time: entryTime,
          side: asString(f.side).trim() || "long",
          result: asOptionalString(f.result),
          pnl: parseOptionalFinite(f.pnl),
          kept_pct: parseOptionalFinite(f.kept_pct),
          within_1_bar_pct: parseOptionalFinite(f.within_1_bar_pct),
        },
      ];
    }),
    fragile_count: parseOptionalFinite(row.fragile_count),
    calibration: calibration
      ? { source: asOptionalString(calibration.source), note: asOptionalString(calibration.note) }
      : null,
    parity: parseParity(row.parity),
    currency: parseCurrencyInfo(row.currency),
    cost_stress: parseStrategyRdCostStress(row.cost_stress),
  };
}

function parseFill(raw: unknown): StrategyRdFill | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    bar_time: asOptionalString(row.bar_time),
    direction: asOptionalString(row.direction),
    entry_time: asOptionalString(row.entry_time),
    entry_price: parseOptionalFinite(row.entry_price),
    exit_time: asOptionalString(row.exit_time),
    exit_price: parseOptionalFinite(row.exit_price),
    qty: parseOptionalFinite(row.qty),
    stop: parseOptionalFinite(row.stop),
    target: parseOptionalFinite(row.target),
    result: asOptionalString(row.result)
      ? normalizeStrategyRdForwardResult(asString(row.result))
      : null,
    pnl: parseOptionalFinite(row.pnl),
    r: parseOptionalFinite(row.r),
    bars_to_event: parseOptionalFinite(row.bars_to_event),
    equity_after: parseOptionalFinite(row.equity_after),
    chart: row.chart ? parseChartParams(row.chart) : null,
    leverage: parseOptionalFinite(row.leverage),
    position_value: parseOptionalFinite(row.position_value),
    margin: parseOptionalFinite(row.margin),
    leverage_used: parseOptionalFinite(row.leverage_used),
    free_margin_at_entry: parseOptionalFinite(row.free_margin_at_entry),
    size_capped: row.size_capped === true,
    size_wanted: parseOptionalFinite(row.size_wanted),
    skipped_reason: asOptionalString(row.skipped_reason),
    commission: parseOptionalFinite(row.commission),
    entry_order: asOptionalString(row.entry_order),
    margin_calls: parseMarginCalls(row.margin_calls),
  };
}

function parseEquityCurve(raw: unknown): StrategyRdEquityCurvePoint[] | null {
  if (!Array.isArray(raw)) return null;
  const out: StrategyRdEquityCurvePoint[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) continue;
    const equity = parseOptionalFinite(row.equity);
    if (equity == null) continue;
    out.push({
      t: asOptionalString(row.t),
      date: asOptionalString(row.date),
      pnl: parseOptionalFinite(row.pnl),
      cumulative_pnl: parseOptionalFinite(row.cumulative_pnl),
      equity,
      trade_index: parseOptionalFinite(row.trade_index),
      bar_time: asOptionalString(row.bar_time),
      result: asOptionalString(row.result),
      direction: asOptionalString(row.direction),
    });
  }
  return out;
}

function parsePeriodPnl(raw: unknown): StrategyRdPeriodPnlBucket[] | null {
  if (!Array.isArray(raw)) return null;
  const out: StrategyRdPeriodPnlBucket[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) continue;
    const pnl = parseOptionalFinite(row.pnl);
    if (pnl == null) continue;
    out.push({
      date: asOptionalString(row.date),
      week_start: asOptionalString(row.week_start),
      pnl,
      trade_count: parseOptionalFinite(row.trade_count),
      wins: parseOptionalFinite(row.wins),
      losses: parseOptionalFinite(row.losses),
    });
  }
  return out;
}

function parseClosedFills(raw: unknown): StrategyRdClosedFill[] | null {
  if (!Array.isArray(raw)) return null;
  const out: StrategyRdClosedFill[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) continue;
    out.push({
      bar_time: asOptionalString(row.bar_time),
      t: asOptionalString(row.t),
      pnl: parseOptionalFinite(row.pnl),
      result: asOptionalString(row.result),
      direction: asOptionalString(row.direction),
      chart: row.chart ? parseChartParams(row.chart) : null,
    });
  }
  return out;
}

function parseApplyOverview(raw: unknown): StrategyRdApplyOverview | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    match_count: parseOptionalFinite(row.match_count),
    closed_count: parseOptionalFinite(row.closed_count),
    open_count: parseOptionalFinite(row.open_count),
    skipped_count: parseOptionalFinite(row.skipped_count),
    target_count: parseOptionalFinite(row.target_count),
    stop_count: parseOptionalFinite(row.stop_count),
    win_rate: parseOptionalFinite(row.win_rate),
    stop_rate: parseOptionalFinite(row.stop_rate),
    open_rate: parseOptionalFinite(row.open_rate),
    avg_bars_to_event: parseOptionalFinite(row.avg_bars_to_event),
    median_bars_to_event: parseOptionalFinite(row.median_bars_to_event),
    min_bars_to_event: parseOptionalFinite(row.min_bars_to_event),
    max_bars_to_event: parseOptionalFinite(row.max_bars_to_event),
    avg_risk: parseOptionalFinite(row.avg_risk),
    median_risk: parseOptionalFinite(row.median_risk),
    avg_r: parseOptionalFinite(row.avg_r),
    sum_r: parseOptionalFinite(row.sum_r),
    expectancy_r: parseOptionalFinite(row.expectancy_r ?? row.avg_r),
    profit_factor_r: parseOptionalFinite(row.profit_factor_r),
    avg_win_r: parseOptionalFinite(row.avg_win_r),
    avg_loss_r: parseOptionalFinite(row.avg_loss_r),
    excursion_unit: parseExcursionUnit(row.excursion_unit),
    avg_mfe: parseOptionalFinite(row.avg_mfe),
    median_mfe: parseOptionalFinite(row.median_mfe),
    min_mfe: parseOptionalFinite(row.min_mfe),
    max_mfe: parseOptionalFinite(row.max_mfe),
    avg_mae: parseOptionalFinite(row.avg_mae),
    median_mae: parseOptionalFinite(row.median_mae),
    min_mae: parseOptionalFinite(row.min_mae),
    max_mae: parseOptionalFinite(row.max_mae),
    starting_equity: parseOptionalFinite(row.starting_equity),
    sizing: parseSizing(row.sizing),
    sum_pnl: parseOptionalFinite(row.sum_pnl),
    avg_pnl: parseOptionalFinite(row.avg_pnl),
    expectancy: parseOptionalFinite(row.expectancy ?? row.avg_pnl),
    ending_equity: parseOptionalFinite(row.ending_equity),
    profit_factor: parseOptionalFinite(row.profit_factor),
    avg_win: parseOptionalFinite(row.avg_win),
    avg_loss: parseOptionalFinite(row.avg_loss),
    largest_win: parseOptionalFinite(row.largest_win),
    largest_loss: parseOptionalFinite(row.largest_loss),
    risk_reward_ratio: parseOptionalFinite(row.risk_reward_ratio),
    return_on_starting_equity_pct: parseOptionalFinite(row.return_on_starting_equity_pct),
    max_drawdown: parseOptionalFinite(row.max_drawdown),
    max_drawdown_pct: parseOptionalFinite(row.max_drawdown_pct),
    max_drawdown_r: parseOptionalFinite(row.max_drawdown_r),
    max_win_streak: parseOptionalFinite(row.max_win_streak),
    max_loss_streak: parseOptionalFinite(row.max_loss_streak),
    equity_curve: parseEquityCurve(row.equity_curve),
    daily_pnl: parsePeriodPnl(row.daily_pnl),
    weekly_pnl: parsePeriodPnl(row.weekly_pnl),
    closed_fills: parseClosedFills(row.closed_fills),
    split: parseStrategyRdHoldoutSplit(row.split),
    stability: parseStrategyRdStability(row.stability),
    report: parseStrategyRdReport(row.stability, row.multiple_testing),
    with_realistic_costs: (() => {
      const w = asRecord(row.with_realistic_costs);
      if (!w) return null;
      const c = asRecord(w.costs) ?? {};
      return {
        costs: {
          spread_pct: parseOptionalFinite(c.spread_pct),
          swap_long_pct: parseOptionalFinite(c.swap_long_pct),
          swap_short_pct: parseOptionalFinite(c.swap_short_pct),
        },
        stats: parseStrategyRdHeadline(w.stats),
      };
    })(),
    sweep: parseStrategyRdSweep(row.sweep),
    accounts: parseOptionalFinite(row.accounts),
    warnings: parseStrategyRdBacktestWarnings(row.warnings),
    by_side: asRecord(row.by_side)
      ? {
          long: parseApplyOverview(asRecord(row.by_side)?.long),
          short: parseApplyOverview(asRecord(row.by_side)?.short),
        }
      : null,
  };
}

function parseLastApply(raw: unknown): StrategyRdLastApply | null {
  const row = asRecord(raw);
  if (!row) return null;
  const resultsRaw = Array.isArray(row.results) ? row.results : [];
  return {
    applied_at: asOptionalString(row.applied_at),
    match_count: parseOptionalFinite(row.match_count),
    starting_equity: parseOptionalFinite(row.starting_equity),
    sizing: parseSizing(row.sizing),
    outcome_summary: row.outcome_summary ? parseOutcomeSummary(row.outcome_summary) : null,
    overview: parseApplyOverview(row.overview),
    results: resultsRaw
      .map(parseApplyResult)
      .filter((r): r is StrategyRdApplyResult => r != null),
  };
}

function parseLevels(raw: unknown): StrategyRdAppliedLevels {
  const row = asRecord(raw) ?? {};
  const dir = asString(row.direction).trim().toLowerCase() === "short" ? "short" : "long";
  return {
    entry: asNumber(row.entry, Number.NaN),
    stop: asNumber(row.stop, Number.NaN),
    target: asNumber(row.target, Number.NaN),
    direction: dir,
    risk: asNumber(row.risk, 0),
    atr: Number.isFinite(asNumber(row.atr, Number.NaN)) ? asNumber(row.atr, Number.NaN) : null,
    valid: row.valid !== false,
    invalid_reason: asOptionalString(row.invalid_reason),
  };
}

function parseForwardOutcome(raw: unknown): StrategyRdForwardOutcome {
  const row = asRecord(raw) ?? {};
  const bars = asNumber(row.bars_to_event, Number.NaN);
  return {
    result: normalizeStrategyRdForwardResult(asString(row.result)),
    bars_to_event: Number.isFinite(bars) ? bars : null,
    note: asOptionalString(row.note),
    exit_price: parseOptionalFinite(row.exit_price),
    ...(Array.isArray(row.legs) ? { legs: parseExitLegs(row.legs) } : {}),
    ...(Array.isArray(row.stop_path) ? { stop_path: parseStopPath(row.stop_path) } : {}),
  };
}

function parseStopPath(raw: unknown[]): StrategyRdStopStep[] {
  return raw.flatMap((item) => {
    const step = asRecord(item);
    const price = parseOptionalFinite(step?.price);
    if (!step || price == null) return [];
    return [
      {
        bars: parseOptionalFinite(step.bars) ?? 0,
        time: asOptionalString(step.time),
        price,
        why: asString(step.why).trim() || "trail",
      },
    ];
  });
}

function parseExitLegs(raw: unknown[]): StrategyRdExitLeg[] {
  return raw.flatMap((item) => {
    const leg = asRecord(item);
    const frac = parseOptionalFinite(leg?.frac);
    const price = parseOptionalFinite(leg?.price);
    if (!leg || frac == null || price == null) return [];
    return [
      {
        frac,
        price,
        kind: asString(leg.kind).trim() || "exit",
        bars: parseOptionalFinite(leg.bars),
      },
    ];
  });
}

function parseApplyResult(raw: unknown): StrategyRdApplyResult | null {
  const row = asRecord(raw);
  if (!row) return null;
  const barTime = asString(row.bar_time).trim();
  if (!barTime) return null;
  const directionRaw = asString(row.direction).trim().toLowerCase();
  const direction =
    directionRaw === "short" ? "short" : directionRaw === "neutral" ? "neutral" : "long";
  return {
    bar_time: barTime,
    direction,
    pattern_id: asString(row.pattern_id).trim() || undefined,
    chart: row.chart ? parseChartParams(row.chart) : undefined,
    levels: parseLevels(row.levels),
    forward_outcome: parseForwardOutcome(row.forward_outcome),
    excursion: parseExcursion(row.excursion),
    fill: row.fill != null ? parseFill(row.fill) : null,
  };
}

function parseOutcomeSummary(raw: unknown): StrategyRdOutcomeSummary {
  const row = asRecord(raw) ?? {};
  const open = asNumber(row.open, Number.NaN);
  return {
    target: asNumber(row.target, 0),
    stop: asNumber(row.stop, 0),
    open: Number.isFinite(open) ? open : asNumber(row.neither, 0),
    skipped: asNumber(row.skipped, 0),
    invalid: asNumber(row.invalid, 0),
  };
}

export async function createStrategyRdHypothesis(
  body: StrategyRdCreateHypothesisRequest,
  signal?: AbortSignal
): Promise<StrategyRdHypothesis> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/hypotheses`, {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, "Could not save hypothesis.");
  }
  const payload = await parseJsonOrThrow<unknown>(response, "Could not save hypothesis.");
  const row = asRecord(payload) ?? {};
  const hypothesis = parseHypothesis(row.hypothesis);
  if (!hypothesis) {
    throwFromErrorBody(500, payload, "Could not save hypothesis.");
  }
  return hypothesis;
}

export async function applyStrategyRdHypothesis(
  hypothesisId: string,
  body: StrategyRdApplyHypothesisRequest,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdApplyResponse> {
  const id = hypothesisId.trim();
  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/hypotheses/${encodeURIComponent(id)}/apply`,
    body,
    {
      signal,
      maxWaitMs: 90_000,
      onProgress,
      stopOn: ["ready", "failed"],
      fallbackErrorMessage: "Could not apply rules.",
    }
  );
  const row = payload;
  const resultsRaw = Array.isArray(row.results) ? row.results : [];
  return {
    status: asString(row.status).trim() || "ready",
    hypothesis_id: asString(row.hypothesis_id).trim() || id,
    template: parseRuleTemplate(row.template),
    outcome_summary: parseOutcomeSummary(row.outcome_summary),
    overview: parseApplyOverview(row.overview),
    results: resultsRaw
      .map(parseApplyResult)
      .filter((r): r is StrategyRdApplyResult => r != null),
    starting_equity: parseOptionalFinite(row.starting_equity),
    sizing: parseSizing(row.sizing),
    applied_at: asOptionalString(row.applied_at),
    margin: parseMarginSummary(row.margin),
    currency: parseCurrencyInfo(row.currency),
    parity: parseParity(row.parity),
    warnings:
      parseStrategyRdBacktestWarnings(row.warnings) ??
      parseStrategyRdBacktestWarnings(asRecord(row.overview)?.warnings),
    report_strategy_hint: asRecord(row.report_strategy_hint)
      ? {
          report_strategy_id: asOptionalString(
            asRecord(row.report_strategy_hint)?.report_strategy_id
          ),
          deep_link: asOptionalString(asRecord(row.report_strategy_hint)?.deep_link),
        }
      : undefined,
  };
}

export async function getStrategyRdHypothesis(
  hypothesisId: string,
  signal?: AbortSignal
): Promise<StrategyRdHypothesis> {
  const id = hypothesisId.trim();
  const response = await authenticatedFetch(
    `${STRATEGY_RD_API_BASE}/hypotheses/${encodeURIComponent(id)}`,
    { signal }
  );
  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, "Hypothesis not found.");
  }
  const payload = await parseJsonOrThrow<unknown>(response, "Could not load hypothesis.");
  const hypothesis = parseHypothesis(asRecord(payload)?.hypothesis);
  if (!hypothesis) {
    throwFromErrorBody(500, payload, "Could not load hypothesis.");
  }
  return hypothesis;
}

/**
 * The recipe as the rule endpoints read it: slot keys (`near_ob#2`) come from the
 * confluences' order, so send them in the composer's order.
 */
export interface StrategyRdRuleComposer {
  candle_type?: string;
  setup?: unknown;
  confluences?: unknown[];
  entry?: unknown;
  optional_min?: number;
}

async function postStrategyRdRules(
  path: string,
  body: unknown,
  signal: AbortSignal | undefined,
  fallback: string
): Promise<Record<string, unknown>> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}${path}`, {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, fallback);
  }
  return asRecord(await parseJsonOrThrow<unknown>(response, fallback)) ?? {};
}

function asStringMap(raw: unknown): Record<string, string> {
  const row = asRecord(raw) ?? {};
  return Object.fromEntries(
    Object.entries(row).filter((e): e is [string, string] => typeof e[1] === "string")
  );
}

function asExprMap(raw: unknown): Record<string, StrategyRdRuleExpr> {
  const row = asRecord(raw) ?? {};
  return Object.fromEntries(
    Object.entries(row).flatMap(([k, v]) => (asRecord(v) ? [[k, asRecord(v)!]] : []))
  );
}

export function parseStrategyRdRuleVocabulary(raw: unknown): StrategyRdRuleVocabulary {
  const row = asRecord(raw) ?? {};
  const sources: Record<string, string[] | StrategyRdRuleVocabularySlot> = {};
  for (const [key, value] of Object.entries(asRecord(row.sources) ?? {})) {
    if (Array.isArray(value)) {
      sources[key] = value.map((v) => asString(v)).filter(Boolean);
      continue;
    }
    const slot = asRecord(value);
    if (!slot || !Array.isArray(slot.values)) continue;
    sources[key] = {
      values: slot.values.map((v) => asString(v)).filter(Boolean),
      concept_id: asOptionalString(slot.concept_id),
      name: asOptionalString(slot.name),
    };
  }
  const entry = asRecord(row.entry);
  const filter = asRecord(row.filter);
  return {
    sources,
    not_usable: Array.isArray(row.not_usable)
      ? row.not_usable.map((v) => asString(v).trim()).filter(Boolean)
      : [],
    distances: asStringMap(row.distances),
    operators: asStringMap(row.operators),
    edges: asOptionalString(row.edges),
    examples: asExprMap(row.examples),
    entry: entry
      ? {
          notes: asOptionalString(entry.notes),
          conditions: asStringMap(entry.conditions),
          examples: asExprMap(entry.examples),
        }
      : null,
    filter: filter
      ? { notes: asOptionalString(filter.notes), examples: asExprMap(filter.examples) }
      : null,
    n_of_m: asOptionalString(row.n_of_m),
    manage: asRecord(row.manage) ? asStringMap(row.manage) : null,
    account_limits: asOptionalString(row.account_limits),
  };
}

/** What this recipe's custom rules can use — call again whenever the composer changes. */
export async function fetchStrategyRdRuleVocabulary(
  composer: StrategyRdRuleComposer,
  signal?: AbortSignal
): Promise<StrategyRdRuleVocabulary> {
  const payload = await postStrategyRdRules(
    "/rules/vocabulary",
    { composer },
    signal,
    "Could not load the rule vocabulary."
  );
  return parseStrategyRdRuleVocabulary(payload.vocabulary);
}

/** Check a rule against the recipe: plain-words errors, or the readback + template patch. */
export async function compileStrategyRdRule(
  body: { composer: StrategyRdRuleComposer; role: StrategyRdRuleRole; rule: unknown },
  signal?: AbortSignal
): Promise<StrategyRdRuleCompileResult> {
  const payload = await postStrategyRdRules(
    "/rules/compile",
    body,
    signal,
    "Could not check the rule."
  );
  return {
    ok: payload.ok === true,
    errors: Array.isArray(payload.errors)
      ? payload.errors.map((e) => asString(e)).filter(Boolean)
      : [],
    readback: asOptionalString(payload.readback),
    template_patch: asRecord(payload.template_patch),
  };
}

async function strategyRdRequest(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal },
  fallback: string
): Promise<Record<string, unknown>> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}${path}`, {
    method: init.method ?? "GET",
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    signal: init.signal,
  });
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throwIfStrategyRdDisabled(response, errBody);
    throwFromErrorBody(response.status, errBody, fallback);
  }
  return asRecord(await parseJsonOrThrow<unknown>(response, fallback)) ?? {};
}

export type StrategyRdPineExport = {
  pinescripts: StrategyRdPineScript[];
  pine_notes: string[];
  chart_timeframe?: string | null;
  position_mode?: string | null;
  /** The backtest the script was built from. */
  applied_at?: string | null;
  title?: string | null;
};

/**
 * Pine Script for the last backtest, no strategy created (§11.1). 400
 * `strategy_rd.confirm_requires_apply` = no backtest yet; 422 `pine_not_translatable`.
 */
export async function getStrategyRdPineScript(
  hypothesisId: string,
  body: { kind: "strategy" | "indicator"; name?: string },
  signal?: AbortSignal
): Promise<StrategyRdPineExport> {
  const payload = await strategyRdRequest(
    `/hypotheses/${encodeURIComponent(hypothesisId.trim())}/pine`,
    { method: "POST", body, signal },
    "Could not build the Pine Script."
  );
  return {
    pinescripts: parsePineScripts(payload.pinescripts),
    pine_notes: Array.isArray(payload.pine_notes)
      ? payload.pine_notes.map((n) => asString(n)).filter(Boolean)
      : [],
    chart_timeframe: asOptionalString(payload.chart_timeframe),
    position_mode: asOptionalString(payload.position_mode),
    applied_at: asOptionalString(payload.applied_at),
    title: asOptionalString(payload.title),
  };
}

/** All saved backtests, newest first (at most 100 — past that, creating one returns 409). */
export async function listStrategyRdHypotheses(signal?: AbortSignal): Promise<StrategyRdHypothesis[]> {
  const payload = await strategyRdRequest("/hypotheses", { signal }, "Could not load saved backtests.");
  const rows = Array.isArray(payload.hypotheses) ? payload.hypotheses : [];
  return rows.map(parseHypothesis).filter((h): h is StrategyRdHypothesis => h != null);
}

/** Every version of this backtest's strategy (same root), oldest first. */
export async function listStrategyRdHypothesisVersions(
  hypothesisId: string,
  signal?: AbortSignal
): Promise<StrategyRdHypothesisVersion[]> {
  const payload = await strategyRdRequest(
    `/hypotheses/${encodeURIComponent(hypothesisId.trim())}/versions`,
    { signal },
    "Could not load versions."
  );
  const rows = Array.isArray(payload.versions) ? payload.versions : [];
  return rows.flatMap((item) => {
    const r = asRecord(item);
    const id = asString(r?.id).trim();
    if (!r || !id) return [];
    return [
      {
        id,
        name: asString(r.name).trim() || id,
        parent_id: asOptionalString(r.parent_id),
        root_id: asOptionalString(r.root_id),
        version: parseOptionalFinite(r.version),
        created_by: asOptionalString(r.created_by),
        created_at: asOptionalString(r.created_at),
        updated_at: asOptionalString(r.updated_at),
        stats: asRecord(r.stats) ? parseStrategyRdHeadline(r.stats) : null,
        split: parseStrategyRdHoldoutSplit(r.split),
      },
    ];
  });
}

export type StrategyRdTradesPage = {
  total: number;
  offset: number;
  /** False: only the first 100 trades are stored (older backtest / storage unavailable). */
  complete: boolean;
  applied_at?: string | null;
  trades: StrategyRdApplyResult[];
};

export async function listStrategyRdHypothesisTrades(
  hypothesisId: string,
  opts: { offset?: number; limit?: number; result?: string; direction?: string } = {},
  signal?: AbortSignal
): Promise<StrategyRdTradesPage> {
  const q = new URLSearchParams();
  q.set("offset", String(opts.offset ?? 0));
  q.set("limit", String(opts.limit ?? 1000));
  if (opts.result) q.set("result", opts.result);
  if (opts.direction) q.set("direction", opts.direction);
  const payload = await strategyRdRequest(
    `/hypotheses/${encodeURIComponent(hypothesisId.trim())}/trades?${q.toString()}`,
    { signal },
    "Could not load trades."
  );
  const rows = Array.isArray(payload.trades) ? payload.trades : [];
  return {
    total: parseOptionalFinite(payload.total) ?? rows.length,
    offset: parseOptionalFinite(payload.offset) ?? 0,
    complete: payload.complete !== false,
    applied_at: asOptionalString(payload.applied_at),
    trades: rows.map(parseApplyResult).filter((r): r is StrategyRdApplyResult => r != null),
  };
}

/** Every trade of the last backtest, fetched 1000 at a time. */
export async function listAllStrategyRdHypothesisTrades(
  hypothesisId: string,
  signal?: AbortSignal
): Promise<Omit<StrategyRdTradesPage, "offset">> {
  let offset = 0;
  const trades: StrategyRdApplyResult[] = [];
  let first: StrategyRdTradesPage | null = null;
  // Guard against a moving total: at most 50 pages.
  for (let i = 0; i < 50; i += 1) {
    const page = await listStrategyRdHypothesisTrades(hypothesisId, { offset, limit: 1000 }, signal);
    first ??= page;
    trades.push(...page.trades);
    offset += page.trades.length;
    if (page.trades.length === 0 || offset >= page.total) break;
  }
  return {
    total: first?.total ?? trades.length,
    complete: first?.complete ?? true,
    applied_at: first?.applied_at ?? null,
    trades,
  };
}

/**
 * The chat's result card for a finished backtest — the FE twin of BE `quant_eval.card`, for
 * TES jobs that finish after the reply (the reply only carries cards for jobs already done).
 */
export async function getStrategyRdResultCard(
  hypothesisId: string,
  signal?: AbortSignal
): Promise<StrategyRdCoachResultCard | null> {
  const payload = await strategyRdRequest(
    `/hypotheses/${encodeURIComponent(hypothesisId.trim())}`,
    { signal },
    "Could not load the backtest."
  );
  return strategyRdResultCardFromHypothesis(asRecord(payload.hypothesis));
}

export function strategyRdResultCardFromHypothesis(
  hyp: Record<string, unknown> | null
): StrategyRdCoachResultCard | null {
  const last = asRecord(hyp?.last_apply);
  const ov = asRecord(last?.overview) ?? {};
  const id = asString(hyp?.id).trim();
  if (!hyp || !id || !asOptionalString(last?.applied_at)) return null;
  const curve = (Array.isArray(ov.equity_curve) ? ov.equity_curve : []).flatMap((p) => {
    const row = asRecord(p);
    const t = asOptionalString(row?.t);
    const equity = parseOptionalFinite(row?.equity);
    return t && equity != null ? [{ t, equity }] : [];
  });
  const n = 60;
  const sampled =
    curve.length <= n
      ? curve
      : Array.from({ length: n }, (_, i) => curve[Math.round((i * (curve.length - 1)) / (n - 1))]);
  const bySide = asRecord(ov.by_side);
  const rob = asRecord(hyp.last_robustness);
  return {
    hypothesis_id: id,
    name: asOptionalString(hyp.name),
    version: parseOptionalFinite(hyp.version) ?? 1,
    stats: parseStrategyRdHeadline(ov),
    by_side: bySide
      ? Object.fromEntries(Object.entries(bySide).map(([k, v]) => [k, parseStrategyRdHeadline(v)]))
      : null,
    split: parseStrategyRdHoldoutSplit(ov.split),
    stability: parseStrategyRdStability(ov.stability),
    equity_curve: sampled,
    warnings: null,
    robustness:
      rob && asOptionalString(rob.verdict)
        ? {
            verdict: asOptionalString(rob.verdict),
            stale: asOptionalString(rob.applied_at) !== asOptionalString(last?.applied_at),
            cost_stress: parseStrategyRdCostStressVerdict(rob.cost_stress),
          }
        : null,
    sweep: parseStrategyRdSweep(ov.sweep),
  };
}

/** Delete a saved backtest (and its stored trades). */
export async function deleteStrategyRdHypothesis(hypothesisId: string): Promise<void> {
  await strategyRdRequest(
    `/hypotheses/${encodeURIComponent(hypothesisId.trim())}`,
    { method: "DELETE" },
    "Could not delete the backtest."
  );
}

/** 409 at 100 saved backtests — nothing is deleted automatically. */
export function isStrategyRdHypothesisLimitError(err: unknown): boolean {
  return (
    Boolean(err) &&
    typeof err === "object" &&
    (err as { slug?: unknown }).slug === "strategy_rd.hypothesis_limit"
  );
}

function parseRuleBlock(raw: unknown): StrategyRdRuleBlock | null {
  const r = asRecord(raw);
  const id = asString(r?.id).trim();
  if (!r || !id) return null;
  return {
    id,
    name: asString(r.name).trim() || id,
    note: asOptionalString(r.note),
    role: asString(r.role).trim(),
    rule: asRecord(r.rule) ?? {},
    readback: asOptionalString(r.readback),
    requires: Array.isArray(r.requires)
      ? r.requires.flatMap((q) => {
          const row = asRecord(q);
          const key = asString(row?.key).trim();
          return row && key
            ? [{ key, concept_id: asOptionalString(row.concept_id), kind: asOptionalString(row.kind) }]
            : [];
        })
      : [],
    created_at: asOptionalString(r.created_at),
    updated_at: asOptionalString(r.updated_at),
  };
}

/** The user's saved rules, newest first (optionally one role). */
export async function listStrategyRdRuleBlocks(
  role?: string | null,
  signal?: AbortSignal
): Promise<StrategyRdRuleBlock[]> {
  const q = role ? `?role=${encodeURIComponent(role)}` : "";
  const payload = await strategyRdRequest(`/rules/blocks${q}`, { signal }, "Could not load your rules.");
  const rows = Array.isArray(payload.blocks) ? payload.blocks : [];
  return rows.map(parseRuleBlock).filter((b): b is StrategyRdRuleBlock => b != null);
}

/** Save a rule, checked against the recipe it was built for (400 = duplicate name / doesn't fit). */
export async function createStrategyRdRuleBlock(body: {
  name: string;
  role: string;
  rule: unknown;
  composer: StrategyRdRuleComposer;
  note?: string;
}): Promise<StrategyRdRuleBlock> {
  const payload = await strategyRdRequest(
    "/rules/blocks",
    { method: "POST", body },
    "Could not save the rule."
  );
  const block = parseRuleBlock(payload.block);
  if (!block) throwFromErrorBody(500, payload, "Could not save the rule.");
  return block;
}

export async function updateStrategyRdRuleBlock(
  blockId: string,
  body: { name?: string; note?: string }
): Promise<StrategyRdRuleBlock> {
  const payload = await strategyRdRequest(
    `/rules/blocks/${encodeURIComponent(blockId)}`,
    { method: "PATCH", body },
    "Could not rename the rule."
  );
  const block = parseRuleBlock(payload.block);
  if (!block) throwFromErrorBody(500, payload, "Could not rename the rule.");
  return block;
}

export async function deleteStrategyRdRuleBlock(blockId: string): Promise<void> {
  await strategyRdRequest(
    `/rules/blocks/${encodeURIComponent(blockId)}`,
    { method: "DELETE" },
    "Could not delete the rule."
  );
}

/** Fit a saved rule to this recipe (maps its confluences to this recipe's ones, then compiles). */
export async function fitStrategyRdRuleBlock(
  blockId: string,
  composer: StrategyRdRuleComposer,
  remap?: Record<string, string> | null
): Promise<StrategyRdRuleBlockUse> {
  const payload = await strategyRdRequest(
    `/rules/blocks/${encodeURIComponent(blockId)}/use`,
    { method: "POST", body: { composer, ...(remap ? { remap } : {}) } },
    "Could not use the rule."
  );
  const choicesRaw = asRecord(payload.choices) ?? {};
  return {
    ok: payload.ok === true,
    errors: Array.isArray(payload.errors) ? payload.errors.map((e) => asString(e)).filter(Boolean) : [],
    readback: asOptionalString(payload.readback),
    template_patch: asRecord(payload.template_patch),
    mapping: Object.fromEntries(
      Object.entries(asRecord(payload.mapping) ?? {}).map(([k, v]) => [k, asString(v)])
    ),
    choices: Object.fromEntries(
      Object.entries(choicesRaw).map(([k, v]) => [
        k,
        Array.isArray(v) ? v.map((x) => asString(x)).filter(Boolean) : [],
      ])
    ),
  };
}

const ROBUSTNESS_POLL_OPTIONS = {
  // 10 reruns of the scan: ~15–30 s for a year of 1h bars, longer on fine TFs.
  maxWaitMs: 600_000,
  stopOn: ["ready", "failed"] as const,
  fallbackErrorMessage: "Robustness test failed.",
};

/**
 * Start the robustness test for the hypothesis's current backtest and poll it.
 * While one is queued / running the BE returns that same job.
 */
export async function runStrategyRdRobustness(
  hypothesisId: string,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdRobustness | null> {
  const id = hypothesisId.trim();
  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/hypotheses/${encodeURIComponent(id)}/robustness`,
    {},
    { ...ROBUSTNESS_POLL_OPTIONS, signal, onProgress }
  );
  return parseStrategyRdRobustness(payload);
}

/** Keep polling a robustness job that is already queued (`last_robustness.job_id`). */
export async function pollStrategyRdRobustness(
  jobId: string,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdRobustness | null> {
  const payload = await pollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/jobs/${encodeURIComponent(jobId.trim())}`,
    "robustness",
    { ...ROBUSTNESS_POLL_OPTIONS, signal, onProgress }
  );
  return parseStrategyRdRobustness(payload);
}

function parsePineScripts(raw: unknown): StrategyRdPineScript[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const row = asRecord(item);
    const script = asString(row?.script);
    if (!row || !script.trim()) return [];
    return [{ side: asString(row.side).trim() || "both", script }];
  });
}

function parseConfirmResponse(raw: unknown, hypothesisId: string): StrategyRdConfirmResponse {
  const row = asRecord(raw) ?? {};
  const confirmation = asRecord(row.confirmation);
  const hint = asRecord(row.report_strategy_hint);
  return {
    status: asString(row.status).trim() || "ready",
    hypothesis_id: asString(row.hypothesis_id).trim() || hypothesisId,
    report_strategy_id: asString(row.report_strategy_id).trim(),
    policy_version_id: asOptionalString(row.policy_version_id),
    mapping_status: asOptionalString(row.mapping_status),
    name: asOptionalString(row.name),
    has_pine: row.has_pine === true,
    pine_parse_status: asOptionalString(row.pine_parse_status),
    pine_notes: parseStrategyRdPineExportInfo(row)?.pineNotes ?? [],
    chart_timeframe: parseStrategyRdPineExportInfo(row)?.chartTimeframe ?? null,
    has_research_sim: row.has_research_sim === true,
    research_sim_fill_count: parseOptionalFinite(row.research_sim_fill_count),
    indicator_export_available: row.indicator_export_available === true,
    position_mode: asOptionalString(row.position_mode),
    pinescripts: parsePineScripts(row.pinescripts),
    robustness: parseStrategyRdRobustness(row.robustness),
    confirmation: confirmation
      ? {
          deep_link: asOptionalString(confirmation.deep_link),
          note: asOptionalString(confirmation.note),
          overview: parseApplyOverview(confirmation.overview),
          outcome_summary: confirmation.outcome_summary
            ? parseOutcomeSummary(confirmation.outcome_summary)
            : null,
          validation: asOptionalString(confirmation.validation),
        }
      : null,
    report_strategy_hint: hint
      ? {
          report_strategy_id: asOptionalString(hint.report_strategy_id),
          deep_link: asOptionalString(hint.deep_link),
        }
      : undefined,
  };
}

export async function confirmStrategyRdHypothesis(
  hypothesisId: string,
  body: StrategyRdConfirmRequest = {},
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdConfirmResponse> {
  const id = hypothesisId.trim();
  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/hypotheses/${encodeURIComponent(id)}/confirm`,
    {
      name: body.name?.trim() || null,
      set_as_current: body.set_as_current !== false,
    },
    {
      signal,
      maxWaitMs: 90_000,
      onProgress,
      stopOn: ["ready", "failed"],
      fallbackErrorMessage: "Could not save as strategy.",
    }
  );
  return parseConfirmResponse(payload, id);
}

function parseResearchSimPayload(raw: unknown): StrategyRdResearchSimPayload {
  const row = asRecord(raw) ?? {};
  const fillsRaw = Array.isArray(row.fills) ? row.fills : [];
  return {
    schema: asOptionalString(row.schema),
    hypothesis_id: asOptionalString(row.hypothesis_id),
    pattern_id: asOptionalString(row.pattern_id),
    scan_context: row.scan_context ? parseScanContext(row.scan_context) : null,
    rule_template: row.rule_template ? parseRuleTemplate(row.rule_template) : null,
    sizing: parseSizing(row.sizing),
    starting_equity: parseOptionalFinite(row.starting_equity),
    overview: parseApplyOverview(row.overview),
    fills: fillsRaw.map(parseFill).filter((f): f is StrategyRdFill => f != null),
    confirmed_at: asOptionalString(row.confirmed_at),
    pinescript_by_side: asRecord(row.pinescript_by_side)
      ? Object.fromEntries(
          Object.entries(asRecord(row.pinescript_by_side) ?? {}).filter(
            (e): e is [string, string] => typeof e[1] === "string" && Boolean(e[1].trim())
          )
        )
      : null,
  };
}

export async function getReportStrategyResearchSim(
  strategyId: string,
  signal?: AbortSignal
): Promise<StrategyRdResearchSimResponse> {
  const id = strategyId.trim();
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(id)}/research-sim`,
    { signal }
  );
  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwFromErrorBody(response.status, errBody, "Research sim not found.");
  }
  const payload = await parseJsonOrThrow<unknown>(response, "Could not load Research sim.");
  const row = asRecord(payload) ?? {};
  const fills = Array.isArray(asRecord(row.research_sim)?.fills)
    ? (asRecord(row.research_sim)?.fills as unknown[])
    : [];
  return {
    status: asString(row.status).trim() || "ready",
    strategy_id: asString(row.strategy_id).trim() || id,
    has_research_sim: row.has_research_sim !== false,
    research_sim_fill_count:
      parseOptionalFinite(row.research_sim_fill_count) ?? fills.length,
    research_sim: parseResearchSimPayload(row.research_sim),
  };
}

export async function exportReportStrategyResearchIndicator(
  strategyId: string,
  signal?: AbortSignal
): Promise<{
  indicator_pine_source: string;
  strategy?: Record<string, unknown> | null;
  pine_notes: string[];
  chart_timeframe: string | null;
}> {
  const id = strategyId.trim();
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(id)}/research-pine/indicator`,
    {
      method: "POST",
      body: JSON.stringify({}),
      signal,
    }
  );
  if (response.status === 404) {
    const errBody = await response.json().catch(() => ({}));
    throwFromErrorBody(response.status, errBody, "Could not export indicator.");
  }
  const payload = await parseJsonOrThrow<unknown>(response, "Could not export indicator.");
  const row = asRecord(payload) ?? {};
  const nested = asRecord(row.strategy);
  const pine =
    asOptionalString(row.indicator_pine_source) ||
    asOptionalString(row.indicator_pinescript) ||
    asOptionalString(nested?.indicator_pine_source) ||
    asOptionalString(nested?.indicator_pinescript) ||
    "";
  const info = parseStrategyRdPineExportInfo(row);
  return {
    indicator_pine_source: pine,
    strategy: nested,
    pine_notes: info?.pineNotes ?? [],
    chart_timeframe: info?.chartTimeframe ?? null,
  };
}

export function isStrategyRdApiError(err: unknown): err is ApiRequestError {
  return err instanceof ApiRequestError;
}
